import { Fragment, useEffect, useState, type CSSProperties } from "react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { Recycle, ChevronDown, ChevronRight } from "lucide-react";
import { theme, card, emptyFrame, inputStyle, btnPrimary } from "../theme";
import { IndexTabs } from "../components/Drawing";
import { StandardHeader } from "../components/standards/StandardHeader";
import { api } from "../api/client";

import { cbPalette } from "../lib/colourBlind";
// ── Static cross-jurisdiction data protection mapping ────────────────────────
// The data-protection counterpart of ControlMapping.tsx. Each row is a data
// protection obligation; each column is a privacy law the platform tracks as
// legal obligations (Control.frameworkId = "legal", Control.domain = the exact
// regulation string in Organization.regulations). A cell holds the citation in
// that law and, where the platform seeds an obligation for it, the seeded
// control id so the org's Legal Obligations status can be overlaid.
//
// Citations were checked against the primary texts for the Crown Dependency
// laws (jerseylaw.je, DPL 2017 explanatory material) and, for the UK, against
// legislation.gov.uk as amended by the Data (Use and Access) Act 2025 (main
// changes in force 5 February 2026, S.I. 2026/82; s. 164A from 19 June 2026;
// the Information Commission from 30 September 2026, S.I. 2026/1015). Where a
// law's numbering is uncertain or has recently been re-enacted (Mexico 2025,
// Vietnam 2026) the cell carries a short descriptive label instead of an
// article number. A cell whose provision changed carries `inForce` / `replaced`
// so the page says since when it reads that way.
//
// A seeded obligation shows its status on a cell only when no other row of the
// same law points at it (`sharedControlIds`): one register entry covering
// principles, consent and accountability must not colour all three, and a
// shared entry never feeds the evidence reuse tab.

type TopicKey =
  | "principles" | "lawful_basis" | "consent" | "purpose" | "retention" | "special_cat" | "children"
  | "transparency" | "rights_handling" | "access" | "erasure" | "other_rights" | "automated"
  | "complaints" | "exemptions"
  | "marketing" | "cookies"
  | "accountability" | "joint_controllers" | "dpo" | "ropa" | "dpia" | "prior_consultation" | "by_design"
  | "processor" | "processor_duties" | "registration" | "representative"
  | "security" | "breach_regulator" | "breach_individuals"
  | "transfers" | "penalties";

interface Topic {
  key: TopicKey;
  topic: string;
  category: string;
  /** Where the laws diverge — shown when the row is expanded. */
  note: string;
}

interface Cell {
  ref: string;
  /** Seeded legal Control id (see backend/prisma/seed.ts LEGAL_OBLIGATIONS) when one exists. */
  controlId?: string;
  /** Date this wording of the law took effect, when it changed recently ("5 Feb 2026"). */
  inForce?: string;
  /** What the provision replaced, shown beside `inForce` ("replaced Art. 22"). */
  replaced?: string;
}

type Region = "UK & Crown Dependencies" | "Europe" | "Americas" | "Asia-Pacific" | "Middle East & Africa";

interface DpLaw {
  id: string;
  label: string;
  short: string;
  region: Region;
  jurisdiction: string;
  regulator: string;
  color: string;
  /** Exact Organization.regulations strings (= seeded Control.domain) that mean this law applies. */
  regulations: string[];
  refs: Partial<Record<TopicKey, Cell>>;
}

const c = (ref: string, controlId?: string, changed?: { inForce: string; replaced?: string }): Cell => ({
  ref,
  ...(controlId ? { controlId } : {}),
  ...(changed ?? {}),
});

const TOPICS: Topic[] = [
  // ── Principles & lawfulness ──
  { key: "principles", topic: "Processing principles", category: "Principles & lawfulness",
    note: "Every law here rests on a variant of the same core principles — lawfulness and fairness, purpose limitation, minimisation, accuracy, storage limitation, security and accountability. GDPR-family laws (EU, UK, Jersey, Guernsey, Isle of Man, DIFC, ADGM) list them almost verbatim; APEC-style laws (Singapore, Malaysia, Hong Kong, Australia, New Zealand) express them as numbered obligations or principles. CCPA has no principles clause, but CPRA added minimisation and purpose limitation in 2023." },
  { key: "lawful_basis", topic: "Lawful basis for processing", category: "Principles & lawfulness",
    note: "GDPR-family laws require one of six lawful bases for every processing activity; the UK has seven since 5 February 2026, adding recognised legitimate interests (UK GDPR Art. 6(1)(ea) with the conditions in Annex 1), which need no balancing test. Consent-centric regimes (China, Korea, Thailand, Malaysia, Saudi Arabia, Qatar, Vietnam) treat consent as the default with narrow exceptions; PIPEDA and Quebec rest on consent for reasonable purposes. CCPA/CPRA and HIPAA have no general lawful-basis test — HIPAA instead lists permitted uses and disclosures. Switzerland only requires a justification where processing would breach personality rights." },
  { key: "consent", topic: "Conditions for valid consent", category: "Principles & lawfulness",
    note: "Most laws now mirror the GDPR standard: freely given, specific, informed, unambiguous and as easy to withdraw as to give. Explicit or written consent is required for sensitive data in Brazil, China, Korea, Malaysia, Thailand, Saudi Arabia and Oman. Consent captured to the GDPR standard generally satisfies the other regimes, subject to local formalities — Korea's separate consents, China's 'separate consent' for transfers and sensitive data, Malaysia's written consent for sensitive data." },
  { key: "purpose", topic: "Purpose limitation & data minimisation", category: "Principles & lawfulness",
    note: "Purpose limitation and minimisation appear everywhere but with different teeth: GDPR Art. 5(1)(b)–(c), Jersey Art. 8(1)(b)–(c) and Guernsey s. 6 are principles enforceable in their own right (further processing: Jersey Art. 13, and in the UK since 5 February 2026 Art. 8A with the compatible purposes in Annex 2); CPRA §1798.100(c) made minimisation an explicit CCPA duty; HIPAA's 'minimum necessary' standard (§164.502(b)) is the healthcare analogue. A single inventory-driven minimisation review evidences all of them." },
  { key: "retention", topic: "Retention limitation & disposal", category: "Principles & lawfulness",
    note: "All regimes require deletion or anonymisation once the purpose is spent; a few add fixed periods. HIPAA requires six-year retention of compliance documentation, Korea PIPA requires destruction within five days of the retention period ending, Singapore requires retention to cease as soon as the purpose is served, and GLBA's Safeguards Rule requires disposal within two years of last use. One retention schedule with a jurisdiction column satisfies them all." },
  { key: "special_cat", topic: "Special category / sensitive data", category: "Principles & lawfulness",
    note: "The categories differ. GDPR-family laws list racial or ethnic origin, politics, religion, trade-union membership, genetics, biometrics, health and sex life or orientation; Jersey and Guernsey add criminal data to the list itself. CCPA's 'sensitive personal information' adds government identifiers, account credentials, precise geolocation and contents of communications. Singapore, Hong Kong, New Zealand and India define no special categories — heightened protection comes from regulator guidance and the reasonableness standard." },
  { key: "children", topic: "Children's data", category: "Principles & lawfulness",
    note: "The age of digital consent varies: 16 under GDPR (member states may lower it to 13 — Spain 14, France 15), 13 in the UK, Jersey and Guernsey, under 13 for COPPA in the US, 14 in China, Korea and Quebec, and 18 under India's DPDP Act (verifiable parental consent for every minor). The UK Children's Code and Australia's Children's Online Privacy Code add design duties for services likely to be accessed by children; since 5 February 2026 UK GDPR Art. 25(1A)–(1B) also makes the children's higher protection matters part of data protection by design for those services." },
  // ── Individual rights ──
  { key: "transparency", topic: "Privacy notice & transparency", category: "Individual rights",
    note: "Every law requires a privacy notice at or before collection. GDPR-family notices (Arts. 12–14) are the most prescriptive and, once written, usually satisfy the others. CCPA adds a 'notice at collection' with prescribed headings and a 'Do Not Sell or Share' link, HIPAA mandates a Notice of Privacy Practices in a set form, and Brazil, China, Thailand and Korea require recipients and retention periods to be stated explicitly." },
  { key: "rights_handling", topic: "Handling rights requests", category: "Individual rights",
    note: "Answering any rights request is a duty in itself: make requests easy to exercise, verify identity where in doubt, act within the time limit and, when refusing or taking no action, say why and how to complain. EU GDPR Art. 12(2)–(6); UK GDPR Art. 12 with Art. 12A (since 5 February 2026: one month from the latest of receipt, identity information or any fee, extendable by two months with reasons given within the first month); Jersey Art. 27 (four weeks, extendable by eight, and a reasoned four-week notice where no action is taken, Art. 27(4)). Mapped for the EU, UK and Jersey so far." },
  { key: "access", topic: "Right of access", category: "Individual rights",
    note: "A right of access exists in every regime except GLBA. Deadlines differ: one month (EU/UK GDPR, Guernsey, Isle of Man; extendable by two months, and in the UK since 5 February 2026 the month runs only from the latest of receipt, identity information or any fee, Art. 12A), four weeks in Jersey (Art. 27(1); extendable by a further eight weeks, Art. 27(2)), 45 days (CCPA), 30 days (PIPEDA, Quebec, Korea, Philippines), 15 days (Brazil, Saudi Arabia), 21 days (Malaysia), 40 days (Hong Kong), 20 working days (New Zealand) and a 'reasonable period' in Australia. Four weeks is always shorter than a calendar month, so design the DSAR process around the shortest applicable deadline. UK GDPR Art. 15(1A) limits the search to what is reasonable and proportionate (retrospective to 1 January 2024)." },
  { key: "erasure", topic: "Right to erasure / deletion", category: "Individual rights",
    note: "GDPR-family laws give a qualified right to erasure; CCPA gives a right to delete with statutory exceptions; Brazil, China, Thailand, the Philippines, Korea, South Africa, Turkey, the UAE and Saudi Arabia have comparable rights. PIPEDA, Australia, New Zealand, Singapore and HIPAA have no free-standing deletion right — deletion follows from withdrawing consent or from retention limits. Erasure must usually cascade to processors (GDPR Art. 17(2), CCPA §1798.105(c))." },
  { key: "other_rights", topic: "Rectification, restriction, portability & objection", category: "Individual rights",
    note: "Rectification and objection exist almost universally. Restriction and portability are GDPR-family features that have spread to Brazil, Thailand, the UAE, DIFC, Malaysia (2024 amendment) and Quebec (from September 2024). CCPA adds a right to correct (2023) and a right to non-discrimination for exercising rights. Korea introduced portability in 2024; Singapore's portability obligation is enacted but not yet in force." },
  { key: "automated", topic: "Automated decision-making & profiling", category: "Individual rights",
    note: "EU GDPR Art. 22, Jersey Art. 38 and Guernsey s. 24 restrict solely automated decisions with legal or similarly significant effects. The UK replaced Art. 22 with Arts. 22A–22D on 5 February 2026: such decisions are permitted with the Art. 22C safeguards (information, representations, human intervention, contest), and restricted only where they rest on special category data (Art. 22B). Brazil (Art. 20), China (Art. 24), Korea (Art. 37-2), Quebec (s. 12.1), UAE (Art. 18), South Africa (s. 71) and Turkey (Art. 11) give a right to object or to an explanation; California regulates automated decision-making technology through CPPA regulations; Australia adds transparency duties for automated decisions from December 2026." },
  { key: "complaints", topic: "Complaints and remedies", category: "Individual rights",
    note: "Every GDPR-family law lets a person complain to the authority, go to court and claim compensation (EU GDPR Arts. 77–82; UK GDPR Arts. 77–80 and 82, Art. 81 omitted; Jersey Arts. 68–70). Since 19 June 2026 a UK controller must also handle complaints made to it: facilitate them (for example with an electronic form), acknowledge each within 30 days and tell the complainant the outcome without undue delay (DPA 2018 s. 164A, for complaints received from that date). Mapped for the EU, UK and Jersey so far." },
  { key: "exemptions", topic: "Restrictions and exemptions", category: "Individual rights",
    note: "Rights and transparency duties give way in defined cases (crime, legal privilege, regulatory functions, journalism, research). The EU GDPR leaves restrictions to member state law under Art. 23; the UK lists them in DPA 2018 Schedules 2–4 under UK GDPR Art. 23; Jersey sets them out in Part 7 (Arts. 40–65). An exemption is applied case by case and recorded, never as a blanket policy. Mapped for the EU, UK and Jersey so far." },
  // ── Marketing & e-privacy ──
  { key: "marketing", topic: "Direct marketing opt-out / do-not-sell", category: "Marketing & e-privacy",
    note: "Direct-marketing rules split between data protection and e-privacy law. GDPR-family laws give an absolute right to object (Art. 21(2), Jersey Art. 36); PECR (UK) and the ePrivacy Directive add opt-in consent for electronic marketing (since 5 February 2026 PECR reg. 22(3A) extends the soft opt-in to charities); CCPA gives an opt-out of 'sale or sharing'; Singapore runs a Do Not Call registry; Hong Kong Part 6A requires consent before marketing; Canada's CASL and Australia's Spam Act require opt-in for commercial electronic messages." },
  { key: "cookies", topic: "Cookies & tracking technologies", category: "Marketing & e-privacy",
    note: "Cookie and tracking consent is set by e-privacy law rather than the general statute: PECR Reg. 6 (UK; substituted on 5 February 2026, with the exceptions to consent in Schedule A1), the ePrivacy Directive as implemented nationally (Germany §25 TDDDG, France Art. 82 LIL and the CNIL guidelines), CCPA/CPRA's opt-out of 'sharing' for cross-context behavioural advertising honoured via Global Privacy Control, and Quebec s. 8.1 (tracking and profiling functions off by default). Jersey, Guernsey and the Isle of Man have no dedicated cookie law — the general consent standard applies." },
  // ── Accountability ──
  { key: "accountability", topic: "Accountability & governance programme", category: "Accountability",
    note: "Accountability means demonstrable compliance: documented policies, training, governance and evidence. It is explicit in GDPR Art. 5(2) and 24, Jersey Art. 6, Guernsey s. 31, PIPEDA Principle 1, POPIA s. 8, LGPD Art. 50, Quebec s. 3.2, Australia APP 1.2, HIPAA §164.530 and GLBA's written information security programme. A privacy management system (ISO 27701 style) evidences all of them from one record set." },
  { key: "joint_controllers", topic: "Joint controllers", category: "Accountability",
    note: "Where two or more controllers decide the purposes and means together, they must set out who does what in an arrangement, and a data subject may exercise rights against any of them (EU and UK GDPR Art. 26, Jersey Art. 7). Mapped for the EU, UK and Jersey so far." },
  { key: "dpo", topic: "Data protection officer", category: "Accountability",
    note: "Mandatory DPO thresholds vary widely. GDPR-family laws: public authorities, large-scale monitoring or large-scale special-category processing. Germany: 20 or more staff regularly processing. Spain: listed sectors. Brazil: every controller (relaxed for small businesses). Always required: Singapore, the Philippines, South Africa (Information Officer), Korea (CPO), Quebec (defaults to the CEO), New Zealand (privacy officer), Malaysia from 2024. Voluntary or 'designated individual' only: Switzerland, Australia, Hong Kong, PIPEDA." },
  { key: "ropa", topic: "Records of processing / data inventory", category: "Accountability",
    note: "GDPR-family laws require records of processing (Art. 30, Jersey Art. 14(3), Switzerland Art. 12) with an exemption below 250 staff unless processing is non-occasional, risky or involves special categories — in practice most organisations need one. Brazil (Art. 37), DIFC and Turkey (VERBİS inventory) require records outright; Japan requires records of third-party provision only. CCPA, PIPEDA, Australia and Singapore have no formal register, but their risk-assessment and accountability duties assume an inventory exists." },
  { key: "dpia", topic: "Impact assessment (DPIA / PIA)", category: "Accountability",
    note: "GDPR-family laws require a DPIA before high-risk processing (Art. 35, Jersey Art. 16, Guernsey s. 44, Switzerland Art. 22); consulting the authority on residual high risk is a separate duty (see prior consultation). Comparable duties: Brazil's RIPD, China's PIPIA (mandatory for sensitive data, automated decisions and transfers), India for Significant Data Fiduciaries, Quebec's PIA for any new system or transfer outside the province, CPRA risk assessments, GLBA and HIPAA risk analyses, and Vietnam's assessment dossier filed with the Ministry of Public Security. One DPIA template with jurisdiction annexes covers all of them." },
  { key: "prior_consultation", topic: "Prior consultation with the authority", category: "Accountability",
    note: "When a DPIA shows high risk that the controller cannot mitigate, the authority must be consulted before processing starts (EU and UK GDPR Art. 36, Jersey Art. 17). A completed DPIA does not discharge this duty, which is why it has its own row. Mapped for the EU, UK and Jersey so far." },
  { key: "by_design", topic: "Data protection by design & default", category: "Accountability",
    note: "A statutory duty under GDPR Art. 25, Jersey Art. 15, Guernsey s. 32, Switzerland Art. 7, LGPD Art. 46 §2, DIFC Art. 14 and Quebec s. 9.1 (highest privacy settings by default). Elsewhere it is implied by accountability and security duties (Australia APP 1.2, PIPEDA Principle 1) or regulator guidance. A privacy-by-design gate in the project process evidences every regime." },
  { key: "processor", topic: "Processor / service-provider contracts (controller side)", category: "Accountability",
    note: "Written processor contracts with mandatory terms are required by GDPR Art. 28, Jersey Art. 19, the Guernsey and Isle of Man equivalents, LGPD, Thailand s. 40, DIFC Art. 24 and India s. 8(2). CCPA requires 'service provider' and 'contractor' contracts with specific restrictions; HIPAA requires Business Associate Agreements; GLBA requires service-provider oversight; PIPEDA, Australia and Singapore keep the controller accountable through 'contractual or other means'. A GDPR Art. 28 agreement with a CCPA/HIPAA addendum is the usual consolidated approach." },
  { key: "processor_duties", topic: "Processor's own duties", category: "Accountability",
    note: "A processor carries duties of its own, not only the contract terms the controller must impose: no sub-processor without the controller's authorisation and the same terms flowed down (Art. 28(2)–(4)), processing only on instructions (Art. 29) and its own records of processing (Art. 30(2)) under the EU and UK GDPR; Jersey Arts. 22–23. Mapped for the EU, UK and Jersey so far." },
  { key: "registration", topic: "Regulator registration & fees", category: "Accountability",
    note: "Registration or fees survive in several jurisdictions: the UK (data protection fee payable to the Information Commission, which replaced the Information Commissioner on 30 September 2026, DPA 2018 s. 137), Jersey (Authority Law Arts. 17–18), Guernsey (ss. 39–40 registration and levy), the Isle of Man (registration and fee), Turkey (VERBİS), South Africa (Information Officer registration), Malaysia (prescribed classes of data user), the Philippines (registration of data processing systems), Israel (database registration, narrowed in 2025), DIFC and ADGM (notification and fee) and California (data brokers only). The EU GDPR abolished general notification." },
  { key: "representative", topic: "Local representative (non-established controllers)", category: "Accountability",
    note: "Controllers outside the jurisdiction that target its residents must appoint a local representative under GDPR Art. 27, UK GDPR Art. 27, Guernsey s. 38, Switzerland Art. 14, China PIPL Art. 53, Thailand s. 37(5), Turkey (VERBİS representative) and Korea (domestic agent). Jersey asks less: Art. 4(3) requires a representative in Jersey only of a controller not established there that uses equipment in Jersey other than for transit (Art. 4(2)(b)); a controller that only targets people in Jersey has no representative duty. There is no equivalent in the Americas or under Australian, Singaporean or Indian law." },
  // ── Security & breach ──
  { key: "security", topic: "Security of processing", category: "Security & breach",
    note: "Every regime requires 'appropriate' or 'reasonable' technical and organisational measures. Some are prescriptive: HIPAA's Security Rule (administrative, physical and technical safeguards), GLBA's Safeguards Rule (encryption, MFA, written programme), Korea PIPA's Art. 29 standards, Israel's Data Security Regulations 2017 (tiered by database level) and Switzerland's Data Security Ordinance. ISO 27001 certification is widely accepted as evidence of appropriate security under all of them." },
  { key: "breach_regulator", topic: "Breach notification — regulator", category: "Security & breach",
    note: "Regulator deadlines: 72 hours — EU/UK GDPR, Jersey, Guernsey, Isle of Man, Thailand, Korea, Philippines, Turkey, Malaysia (2024), Saudi Arabia, Oman and India (detailed report). Three calendar days after assessment — Singapore. Three working days — Brazil. 'Immediately' — China. 'As soon as possible/feasible' — Switzerland, South Africa, UAE, PIPEDA, New Zealand. 60 days — HIPAA (HHS); 30 days — GLBA (FTC); 30-day assessment window — Australia. CCPA has no regulator deadline, but California's breach statute requires notice to the Attorney General for 500 or more residents. Build the incident process to the 72-hour standard and it satisfies almost every regime." },
  { key: "breach_individuals", topic: "Breach notification — individuals", category: "Security & breach",
    note: "Notice to individuals is required where the breach is likely to cause high risk (GDPR-family), 'serious harm' (Australia, New Zealand), a 'real risk of significant harm' (PIPEDA), 'significant harm' (Singapore), or unconditionally for defined data (US state breach laws, HIPAA within 60 days, Korea). China and Saudi Arabia require notice unless measures effectively prevent harm; Hong Kong notification remains voluntary; Vietnam and Malaysia require notice for high-risk breaches." },
  // ── Transfers & enforcement ──
  { key: "transfers", topic: "International transfers", category: "Transfers & enforcement",
    note: "Transfer rules range from adequacy-plus-safeguards (EU GDPR, Jersey, Guernsey, Isle of Man, Switzerland, Brazil, UAE, DIFC, ADGM, South Africa) to consent-or-comparable-protection tests (Singapore, Malaysia, Thailand, Philippines, Japan, Australia APP 8, New Zealand IPP 12) to government-gated regimes (China — CAC security assessment, standard contract or certification; Vietnam — transfer dossier; Saudi Arabia — approved jurisdictions; Turkey — Board permission or standard contracts; India — government blacklist). Since 5 February 2026 the UK no longer uses adequacy: Arts. 44 and 45 are omitted, a transfer is approved by regulations (Art. 45A) where the data protection test is met (protection not materially lower, Art. 45B), and Art. 46 safeguards must meet the same test (Art. 46(1A)). Quebec requires a PIA before any transfer outside the province. Keep a transfer register with the mechanism per destination." },
  { key: "penalties", topic: "Administrative fines & enforcement", category: "Transfers & enforcement",
    note: "Maximum fines: EU GDPR EUR 20m or 4% of turnover; UK GDPR GBP 17.5m or 4% (PECR too since 5 February 2026, through reg. 31 and Schedule 1 applying DPA 2018 Parts 5–7); Guernsey GBP 10m (tiered); Isle of Man GBP 1m; Jersey £5m or £10m by category, and no more than the higher of £300,000 or 10% of global turnover (Data Protection Authority Law Art. 27); Brazil 2% of Brazilian revenue capped at R$50m per infraction; China RMB 50m or 5%; Korea 3% of related revenue; Singapore 10% of local turnover or S$1m; Australia A$50m, three times the benefit or 30% of turnover; Quebec C$25m or 4%; India INR 250 crore; South Africa R10m plus criminal liability; CCPA US$2,663 to US$7,988 per violation (inflation-adjusted, the higher rate for intentional and children's violations); HIPAA tiered civil money penalties. Jersey, Guernsey and Turkey also treat certain breaches as criminal offences." },
];

const LAWS: DpLaw[] = cbPalette([
  // ── UK & Crown Dependencies ───────────────────────────────────────────────
  {
    id: "uk_gdpr", label: "UK GDPR", short: "UK GDPR", region: "UK & Crown Dependencies",
    jurisdiction: "United Kingdom", regulator: "Information Commission (formerly ICO)", color: "#6d28d9",
    regulations: ["UK GDPR"],
    refs: {
      principles: c("Art. 5", "legal-uk-gdpr-principles"),
      lawful_basis: c("Art. 6 (incl. 6(1)(ea) recognised legitimate interests, Annex 1)", "legal-uk-gdpr-lawful-basis", { inForce: "5 Feb 2026" }),
      consent: c("Art. 7"),
      purpose: c("Art. 5(1)(b)–(c), 8A / Annex 2", "legal-uk-gdpr-principles", { inForce: "5 Feb 2026" }),
      retention: c("Art. 5(1)(e)", "legal-uk-gdpr-principles"),
      special_cat: c("Art. 9–10", "legal-uk-gdpr-special-cat"),
      children: c("Art. 8 (age 13), 25(1A)–(1B) / Children's Code", "legal-uk-gdpr-children", { inForce: "5 Feb 2026" }),
      transparency: c("Art. 12–14", "legal-uk-gdpr-transparency"),
      rights_handling: c("Art. 12, 12A (time limits)", undefined, { inForce: "5 Feb 2026" }),
      access: c("Art. 12A, 15 (15(1A) reasonable and proportionate search)", "legal-uk-gdpr-dsar", { inForce: "5 Feb 2026" }),
      erasure: c("Art. 17"),
      other_rights: c("Art. 16, 18–21"),
      automated: c("Arts. 22A–22D (safeguards; special category restricted, 22B)", "legal-uk-gdpr-art22", { inForce: "5 Feb 2026", replaced: "Art. 22" }),
      complaints: c("Arts. 77–80, 82"),
      exemptions: c("Art. 23 / DPA 2018 Sch. 2–4"),
      marketing: c("Art. 21(2)–(3)"),
      accountability: c("Art. 5(2), 24", "legal-uk-gdpr-principles"),
      joint_controllers: c("Art. 26"),
      dpo: c("Art. 37–39", "legal-uk-gdpr-dpo"),
      ropa: c("Art. 30", "legal-uk-gdpr-ropa"),
      dpia: c("Art. 35", "legal-uk-gdpr-dpia"),
      prior_consultation: c("Art. 36"),
      by_design: c("Art. 25 (incl. 25(1A)–(1B) children)", undefined, { inForce: "5 Feb 2026" }),
      processor: c("Art. 28", "legal-uk-gdpr-processor"),
      processor_duties: c("Arts. 28(2)–(4), 29, 30(2)"),
      representative: c("Art. 27", "legal-uk-gdpr-art27"),
      security: c("Art. 32"),
      breach_regulator: c("Art. 33 (72 h)", "legal-uk-gdpr-breach"),
      breach_individuals: c("Art. 34", "legal-uk-gdpr-breach"),
      transfers: c("Arts. 44A, 45A–45C, 46(1A), 47A, 49A / IDTA", "legal-uk-gdpr-transfers", { inForce: "5 Feb 2026", replaced: "Arts. 44–45 (adequacy)" }),
      penalties: c("Art. 83"),
    },
  },
  {
    id: "dpa2018", label: "UK Data Protection Act 2018", short: "DPA 2018", region: "UK & Crown Dependencies",
    jurisdiction: "United Kingdom", regulator: "Information Commission (formerly ICO)", color: "#7c3aed",
    regulations: ["Data Protection Act 2018"],
    refs: {
      special_cat: c("Sch. 1", "legal-dpa2018-special-cat"),
      children: c("s. 9 (age 13)"),
      access: c("Sch. 2 (exemptions)", "legal-dpa2018-exemptions"),
      other_rights: c("Sch. 2 (exemptions)", "legal-dpa2018-exemptions"),
      complaints: c("s. 164A (complaints to the controller)", "legal-dpa2018-complaints", { inForce: "19 Jun 2026" }),
      exemptions: c("Sch. 2–4", "legal-dpa2018-exemptions"),
      registration: c("s. 137 (data protection fee)", "legal-dpa2018-ico-reg"),
      penalties: c("Pt 6 (ss. 155–157)", "legal-dpa2018-eir"),
    },
  },
  {
    id: "pecr", label: "UK PECR 2003", short: "PECR", region: "UK & Crown Dependencies",
    jurisdiction: "United Kingdom", regulator: "Information Commission (formerly ICO)", color: "#9333ea",
    regulations: ["Privacy and Electronic Communications Regulations 2003 (PECR)"],
    refs: {
      marketing: c("Regs. 19–24 (22(3A) charity soft opt-in)", "legal-pecr-marketing", { inForce: "5 Feb 2026" }),
      cookies: c("Reg. 6 / Sch. A1 (exceptions to consent)", "legal-pecr-cookies", { inForce: "5 Feb 2026" }),
      security: c("Reg. 5 (service providers)", "legal-pecr-security"),
      breach_regulator: c("Reg. 5A (72 h, service providers)", "legal-pecr-security"),
      penalties: c("Reg. 31 / Sch. 1 (DPA 2018 Pts 5–7 penalties)", undefined, { inForce: "5 Feb 2026", replaced: "the old reg. 31" }),
    },
  },
  {
    id: "jersey", label: "Data Protection (Jersey) Law 2018", short: "Jersey DPJL", region: "UK & Crown Dependencies",
    jurisdiction: "Jersey", regulator: "JOIC", color: "#C026D3",
    regulations: ["Jersey Data Protection Law 2018"],
    refs: {
      principles: c("Art. 8", "legal-jersey-principles"),
      lawful_basis: c("Art. 9 / Sch. 2 Pt 1", "legal-jersey-principles"),
      consent: c("Art. 11", "legal-jersey-principles"),
      purpose: c("Art. 8(1)(b)–(c), 13 (further processing)", "legal-jersey-principles"),
      retention: c("Art. 8(1)(e)", "legal-jersey-principles"),
      special_cat: c("Art. 9 / Sch. 2 Pt 2", "legal-jersey-special-cat"),
      children: c("Art. 11 (age 13)"),
      transparency: c("Art. 10, 12", "legal-jersey-transparency"),
      rights_handling: c("Art. 27 (4 weeks, +8)", "legal-jersey-rights"),
      access: c("Art. 27–28 (4 weeks)", "legal-jersey-rights"),
      erasure: c("Art. 32", "legal-jersey-rights"),
      other_rights: c("Art. 31, 33–35", "legal-jersey-rights"),
      automated: c("Art. 38", "legal-jersey-rights"),
      complaints: c("Arts. 68–70"),
      exemptions: c("Pt 7 (Arts. 40–65)"),
      marketing: c("Art. 36", "legal-jersey-rights"),
      accountability: c("Art. 6", "legal-jersey-principles"),
      joint_controllers: c("Art. 7"),
      dpo: c("Art. 24–26", "legal-jersey-dpo"),
      ropa: c("Art. 14(3)", "legal-jersey-ropa"),
      dpia: c("Art. 16", "legal-jersey-dpia"),
      prior_consultation: c("Art. 17", "legal-jersey-dpia"),
      by_design: c("Art. 15", "legal-jersey-by-design"),
      processor: c("Art. 19", "legal-jersey-processor-ag"),
      processor_duties: c("Arts. 22–23", "legal-jersey-processor-ag"),
      registration: c("Authority Law Art. 17–18", "legal-jersey-registration"),
      representative: c("Art. 4(3) (non-established, using equipment in Jersey)"),
      security: c("Art. 21", "legal-jersey-security"),
      breach_regulator: c("Art. 20 (72 h)", "legal-jersey-breach"),
      breach_individuals: c("Art. 20", "legal-jersey-breach"),
      transfers: c("Art. 66–67 / Sch. 3", "legal-jersey-transfers"),
      penalties: c("Authority Law Art. 26–27 (£5m / £10m; higher of £300k or 10%)"),
    },
  },
  {
    id: "guernsey", label: "Data Protection (Bailiwick of Guernsey) Law 2017", short: "Guernsey DPL", region: "UK & Crown Dependencies",
    jurisdiction: "Bailiwick of Guernsey", regulator: "ODPA", color: "#a21caf",
    regulations: ["Guernsey Data Protection Law 2017"],
    refs: {
      principles: c("s. 6", "legal-guernsey-dpl"),
      lawful_basis: c("s. 7 / Sch. 2", "legal-guernsey-dpl"),
      consent: c("s. 10", "legal-guernsey-dpl"),
      purpose: c("ss. 6(2)(b)–(c), 9", "legal-guernsey-dpl"),
      retention: c("s. 6(2)(e)", "legal-guernsey-dpl"),
      special_cat: c("s. 7 / Sch. 2", "legal-guernsey-special-cat"),
      children: c("s. 10(2)(f) (age 13)"),
      transparency: c("ss. 12–13", "legal-guernsey-dpl"),
      access: c("s. 15", "legal-guernsey-rights"),
      erasure: c("s. 21", "legal-guernsey-rights"),
      other_rights: c("ss. 14, 18–22", "legal-guernsey-rights"),
      automated: c("s. 24", "legal-guernsey-rights"),
      marketing: c("s. 17", "legal-guernsey-rights"),
      accountability: c("s. 31", "legal-guernsey-dpl"),
      dpo: c("ss. 47–51", "legal-guernsey-dpo"),
      ropa: c("s. 37", "legal-guernsey-ropa"),
      dpia: c("ss. 44–45", "legal-guernsey-dpia"),
      by_design: c("s. 32"),
      processor: c("ss. 34–36", "legal-guernsey-processor"),
      registration: c("ss. 39–40 (registration & levy)", "legal-guernsey-registration"),
      representative: c("s. 38"),
      security: c("s. 41", "legal-guernsey-security"),
      breach_regulator: c("s. 42 (72 h)", "legal-guernsey-breach"),
      breach_individuals: c("s. 43", "legal-guernsey-breach"),
      transfers: c("ss. 55–59"),
      penalties: c("ss. 73–74 (max £10m)"),
    },
  },
  {
    id: "iom", label: "Isle of Man Data Protection Act 2018 (Applied GDPR)", short: "IoM DPA", region: "UK & Crown Dependencies",
    jurisdiction: "Isle of Man", regulator: "Information Commissioner", color: "#86198f",
    regulations: ["Isle of Man Data Protection Act 2018"],
    refs: {
      principles: c("Applied GDPR Art. 5", "legal-iom-dpa"),
      lawful_basis: c("Art. 6", "legal-iom-dpa"),
      consent: c("Art. 7", "legal-iom-dpa"),
      purpose: c("Art. 5(1)(b)–(c)", "legal-iom-dpa"),
      retention: c("Art. 5(1)(e)", "legal-iom-dpa"),
      special_cat: c("Art. 9 / Sch. 1", "legal-iom-special-cat"),
      children: c("Art. 8"),
      transparency: c("Art. 12–14", "legal-iom-dpa"),
      access: c("Art. 15", "legal-iom-dsar"),
      erasure: c("Art. 17"),
      other_rights: c("Art. 16, 18–21"),
      automated: c("Art. 22"),
      marketing: c("Art. 21(2)"),
      accountability: c("Art. 5(2), 24", "legal-iom-dpa"),
      dpo: c("Art. 37–39", "legal-iom-dpo"),
      ropa: c("Art. 30", "legal-iom-ropa"),
      dpia: c("Art. 35"),
      by_design: c("Art. 25"),
      processor: c("Art. 28", "legal-iom-processor"),
      registration: c("Registration & fee", "legal-iom-registration"),
      representative: c("Art. 27"),
      security: c("Art. 32"),
      breach_regulator: c("Art. 33 (72 h)", "legal-iom-breach"),
      breach_individuals: c("Art. 34", "legal-iom-breach"),
      transfers: c("Art. 44–49"),
      penalties: c("Art. 83 (max £1m)"),
    },
  },

  // ── Europe ────────────────────────────────────────────────────────────────
  {
    id: "eu_gdpr", label: "EU GDPR", short: "EU GDPR", region: "Europe",
    jurisdiction: "EU / EEA", regulator: "EDPB & national supervisory authorities", color: "#2563eb",
    regulations: ["EU GDPR", "EU GDPR (via EEA Agreement)"],
    refs: {
      principles: c("Art. 5", "legal-eu-gdpr-art5"),
      lawful_basis: c("Art. 6", "legal-eu-gdpr-art6"),
      consent: c("Art. 7", "legal-eu-gdpr-art7"),
      purpose: c("Art. 5(1)(b)–(c)", "legal-eu-gdpr-art5"),
      retention: c("Art. 5(1)(e)", "legal-eu-gdpr-art5"),
      special_cat: c("Art. 9–10", "legal-eu-gdpr-art9"),
      children: c("Art. 8 (16, MS may lower to 13)"),
      transparency: c("Art. 12–14", "legal-eu-gdpr-art13"),
      rights_handling: c("Art. 12(2)–(6)"),
      access: c("Art. 15", "legal-eu-gdpr-art15"),
      erasure: c("Art. 17", "legal-eu-gdpr-art17"),
      other_rights: c("Art. 16, 18–21", "legal-eu-gdpr-art20"),
      automated: c("Art. 22", "legal-eu-gdpr-art22"),
      complaints: c("Arts. 77–82"),
      exemptions: c("Art. 23 (member state law)"),
      marketing: c("Art. 21(2)–(3)", "legal-eu-gdpr-art20"),
      cookies: c("ePrivacy Dir. Art. 5(3)"),
      accountability: c("Art. 5(2), 24", "legal-eu-gdpr-art5"),
      joint_controllers: c("Art. 26", "legal-eu-gdpr-art26"),
      dpo: c("Art. 37–39", "legal-eu-gdpr-art37"),
      ropa: c("Art. 30", "legal-eu-gdpr-art30"),
      dpia: c("Art. 35", "legal-eu-gdpr-art35"),
      prior_consultation: c("Art. 36"),
      by_design: c("Art. 25", "legal-eu-gdpr-art25"),
      processor: c("Art. 28", "legal-eu-gdpr-art28"),
      processor_duties: c("Arts. 28(2)–(4), 29, 30(2)"),
      representative: c("Art. 27", "legal-eu-gdpr-art27"),
      security: c("Art. 32", "legal-eu-gdpr-art32"),
      breach_regulator: c("Art. 33 (72 h)", "legal-eu-gdpr-art33"),
      breach_individuals: c("Art. 34", "legal-eu-gdpr-art33"),
      transfers: c("Art. 44–49", "legal-eu-gdpr-art44"),
      penalties: c("Art. 83–84", "legal-eu-gdpr-art83"),
    },
  },
  {
    id: "ndsg", label: "Swiss Federal Act on Data Protection (revFADP / nDSG)", short: "nDSG", region: "Europe",
    jurisdiction: "Switzerland", regulator: "FDPIC", color: "#dc2626",
    regulations: ["nDSG (revised Federal Act on Data Protection)"],
    refs: {
      principles: c("Art. 6", "legal-ndsg-art5"),
      lawful_basis: c("Art. 30–31 (justification)", "legal-ndsg-art5"),
      consent: c("Art. 6(6)–(7)", "legal-ndsg-art5"),
      purpose: c("Art. 6(3)", "legal-ndsg-art5"),
      retention: c("Art. 6(4)", "legal-ndsg-art5"),
      special_cat: c("Art. 5(c), 6(7)"),
      transparency: c("Art. 19–21", "legal-ndsg-art19"),
      access: c("Art. 25–27"),
      erasure: c("Art. 32"),
      other_rights: c("Art. 28 (portability), 32"),
      automated: c("Art. 21"),
      dpo: c("Art. 10 (voluntary adviser)"),
      ropa: c("Art. 12", "legal-ndsg-ropa"),
      dpia: c("Art. 22–23", "legal-ndsg-art21"),
      by_design: c("Art. 7", "legal-ndsg-art10"),
      processor: c("Art. 9"),
      representative: c("Art. 14–15"),
      security: c("Art. 8", "legal-ndsg-art8"),
      breach_regulator: c("Art. 24 (as soon as possible)", "legal-ndsg-art24"),
      breach_individuals: c("Art. 24(4)", "legal-ndsg-art24"),
      transfers: c("Art. 16–17", "legal-ndsg-art25"),
      penalties: c("Art. 60–63 (criminal, CHF 250k)"),
    },
  },
  {
    id: "bdsg", label: "German Federal Data Protection Act (BDSG)", short: "BDSG", region: "Europe",
    jurisdiction: "Germany (supplements EU GDPR)", regulator: "BfDI & state authorities", color: "#b45309",
    regulations: ["BDSG (Federal Data Protection Act)"],
    refs: {
      lawful_basis: c("§ 26 (employee data)", "legal-bdsg-employee"),
      special_cat: c("§ 22"),
      access: c("§ 34 (restrictions)", "legal-bdsg-dsr"),
      other_rights: c("§§ 32–37 (restrictions)", "legal-bdsg-dsr"),
      automated: c("§§ 31, 37 (scoring)"),
      cookies: c("§ 25 TDDDG", "legal-bdsg-telemed"),
      dpo: c("§ 38 (20+ staff)", "legal-bdsg-dsb"),
      penalties: c("§§ 41–43"),
    },
  },
  {
    id: "lil", label: "Loi Informatique et Libertés (France)", short: "LIL", region: "Europe",
    jurisdiction: "France (supplements EU GDPR)", regulator: "CNIL", color: "#1e40af",
    regulations: ["Loi Informatique et Libertés"],
    refs: {
      lawful_basis: c("Employee data (CSE consultation)", "legal-lil-employees"),
      special_cat: c("Art. 6, 44 (biometrics/health)", "legal-lil-biometric"),
      children: c("Art. 45 (age 15)"),
      cookies: c("Art. 82 / CNIL guidelines", "legal-lil-cookies"),
      registration: c("Art. 66 (health data authorisations)"),
      penalties: c("Art. 20–22 (CNIL sanctions)", "legal-lil-cnil"),
    },
  },
  {
    id: "lopdgdd", label: "LOPDGDD (Spain)", short: "LOPDGDD", region: "Europe",
    jurisdiction: "Spain (supplements EU GDPR)", regulator: "AEPD", color: "#c2410c",
    regulations: ["LOPDGDD"],
    refs: {
      lawful_basis: c("Arts. 8, 87–91 (employees)", "legal-lopdgdd-employee"),
      special_cat: c("Art. 9"),
      children: c("Art. 7 (age 14)", "legal-lopdgdd-minors"),
      dpo: c("Art. 34 (mandatory sectors)", "legal-lopdgdd-dpo"),
      registration: c("Art. 34(3) (DPO notified to AEPD)", "legal-lopdgdd-dpo"),
      penalties: c("Arts. 72–74"),
    },
  },

  // ── Americas ──────────────────────────────────────────────────────────────
  {
    id: "ccpa", label: "California CCPA / CPRA", short: "CCPA", region: "Americas",
    jurisdiction: "California, USA", regulator: "CPPA & Attorney General", color: "#0891b2",
    regulations: ["CCPA / CPRA (California)"],
    refs: {
      principles: c("§1798.100(c) (minimisation)", "legal-cpra-minimise"),
      purpose: c("§1798.100(c)", "legal-cpra-minimise"),
      retention: c("§1798.100(a)(3)", "legal-cpra-minimise"),
      special_cat: c("§1798.121 (sensitive PI)", "legal-ccpa-sensitive"),
      children: c("§1798.120(c) (opt-in under 16)", "legal-ccpa-optout"),
      transparency: c("§1798.100, .130", "legal-ccpa-notice"),
      access: c("§1798.110, .115 (right to know)", "legal-ccpa-know"),
      erasure: c("§1798.105", "legal-ccpa-delete"),
      other_rights: c("§1798.106 (correct), .125", "legal-ccpa-correct"),
      automated: c("§1798.185(a)(16) / ADMT regs"),
      marketing: c("§1798.120 (opt-out of sale/sharing)", "legal-ccpa-optout"),
      cookies: c("§1798.135 (GPC signal)", "legal-ccpa-optout"),
      dpia: c("§1798.185(a)(15) (risk assessments)", "legal-cpra-risk"),
      processor: c("§1798.100(d), .140(ag)", "legal-ccpa-contracts"),
      registration: c("Data brokers: §1798.99.82"),
      security: c("§1798.100(e), .150"),
      breach_regulator: c("Civ. Code §1798.82 (AG, 500+ residents)"),
      breach_individuals: c("Civ. Code §1798.82"),
      penalties: c("§1798.155"),
    },
  },
  {
    id: "hipaa", label: "HIPAA Privacy, Security & Breach Rules", short: "HIPAA", region: "Americas",
    jurisdiction: "USA (covered entities & business associates)", regulator: "HHS Office for Civil Rights", color: "#0e7490",
    regulations: ["HIPAA (healthcare)"],
    refs: {
      lawful_basis: c("§164.502, .506–.510 (permitted uses)", "legal-hipaa-privacy"),
      consent: c("§164.508 (authorisation)", "legal-hipaa-privacy"),
      purpose: c("§164.502(b) (minimum necessary)", "legal-hipaa-minimum"),
      retention: c("§164.530(j) (6-year documentation)"),
      special_cat: c("PHI (§160.103)", "legal-hipaa-privacy"),
      transparency: c("§164.520 (Notice of Privacy Practices)", "legal-hipaa-notice"),
      access: c("§164.524", "legal-hipaa-rights"),
      other_rights: c("§164.522, .526, .528", "legal-hipaa-rights"),
      marketing: c("§164.508(a)(3) (marketing authorisation)", "legal-hipaa-privacy"),
      accountability: c("§164.530 / §164.308", "legal-hipaa-security"),
      dpo: c("§164.530(a), §164.308(a)(2) (officials)", "legal-hipaa-security"),
      dpia: c("§164.308(a)(1)(ii)(A) (risk analysis)", "legal-hipaa-security"),
      processor: c("§164.502(e), .504(e) (BAAs)", "legal-hipaa-baa"),
      security: c("§§164.302–.318 (Security Rule)", "legal-hipaa-techsec"),
      breach_regulator: c("§164.408 (HHS, 60 days)", "legal-hipaa-breach"),
      breach_individuals: c("§164.404 (60 days)", "legal-hipaa-breach"),
      penalties: c("§160.404 (tiered CMPs)", "legal-hipaa-penalty"),
    },
  },
  {
    id: "glba", label: "GLBA Privacy & Safeguards Rules", short: "GLBA", region: "Americas",
    jurisdiction: "USA (financial institutions)", regulator: "FTC, CFPB & banking regulators", color: "#155e75",
    regulations: ["GLBA (financial services)"],
    refs: {
      retention: c("§314.4(c)(6) (disposal within 2 years)", "legal-glba-safeguards"),
      transparency: c("16 CFR Pt 313 / Reg P (privacy notice)", "legal-glba-privacy"),
      marketing: c("§313.7, .10 (opt-out of sharing)", "legal-glba-privacy"),
      accountability: c("§314.4 (written security programme)", "legal-glba-safeguards"),
      dpo: c("§314.4(a) (Qualified Individual)", "legal-glba-safeguards"),
      dpia: c("§314.4(b) (risk assessment)", "legal-glba-risk"),
      processor: c("§314.4(f) (service providers)", "legal-glba-service"),
      security: c("16 CFR Pt 314 (Safeguards Rule)", "legal-glba-safeguards"),
      breach_regulator: c("§314.4(j) (FTC, 30 days, 500+ consumers)", "legal-glba-breach"),
      penalties: c("15 USC §6805 / FTC Act §5"),
    },
  },
  {
    id: "ftc", label: "FTC Act §5, COPPA & CAN-SPAM", short: "FTC §5", region: "Americas",
    jurisdiction: "USA (federal)", regulator: "FTC", color: "#0f766e",
    regulations: ["FTC Act Section 5"],
    refs: {
      consent: c("COPPA §312.5 (parental consent)", "legal-ftc-coppa"),
      children: c("COPPA 16 CFR Pt 312 (under 13)", "legal-ftc-coppa"),
      transparency: c("15 USC §45 (deceptive statements)", "legal-ftc-unfair"),
      marketing: c("CAN-SPAM (15 USC §7701)"),
      security: c("15 USC §45 (unreasonable security)", "legal-ftc-security"),
      breach_regulator: c("Health Breach Notification Rule (16 CFR 318)"),
      breach_individuals: c("Health Breach Notification Rule (16 CFR 318)"),
      penalties: c("15 USC §45(m)", "legal-ftc-unfair"),
    },
  },
  {
    id: "pipeda", label: "PIPEDA (Canada)", short: "PIPEDA", region: "Americas",
    jurisdiction: "Canada (federal, private sector)", regulator: "OPC", color: "#b91c1c",
    regulations: ["PIPEDA"],
    refs: {
      principles: c("Sch. 1 (10 principles)", "legal-pipeda-4-1"),
      lawful_basis: c("Principle 3 / s. 7 (consent + exceptions)", "legal-pipeda-4-3"),
      consent: c("Principle 3 (4.3) / s. 6.1", "legal-pipeda-4-3"),
      purpose: c("Principles 2, 4 (4.2, 4.4)", "legal-pipeda-4-2"),
      retention: c("Principle 5 (4.5)", "legal-pipeda-4-5"),
      special_cat: c("4.3.4, 4.3.6 (sensitivity)", "legal-pipeda-4-3"),
      children: c("OPC guidance (meaningful consent)"),
      transparency: c("Principle 8 (4.8)", "legal-pipeda-4-8"),
      access: c("Principle 9 (4.9) / s. 8", "legal-pipeda-4-9"),
      erasure: c("4.3.8 (withdraw consent)", "legal-pipeda-4-3"),
      other_rights: c("4.9.5 (correction)", "legal-pipeda-4-9"),
      marketing: c("CASL ss. 6–11", "legal-pipeda-marketing"),
      cookies: c("CASL s. 8 / OPC OBA guidance", "legal-pipeda-marketing"),
      accountability: c("Principle 1 (4.1)", "legal-pipeda-4-1"),
      dpo: c("4.1.1 (designated individual)", "legal-pipeda-4-1"),
      processor: c("4.1.3 (contractual means)", "legal-pipeda-4-1"),
      security: c("Principle 7 (4.7)", "legal-pipeda-4-7"),
      breach_regulator: c("s. 10.1 (as soon as feasible)", "legal-pipeda-breach"),
      breach_individuals: c("s. 10.1(3)", "legal-pipeda-breach"),
      transfers: c("4.1.3 / OPC transfer guidelines", "legal-pipeda-transfer"),
      penalties: c("s. 28 (up to C$100k)"),
    },
  },
  {
    id: "qc25", label: "Quebec Law 25 (private-sector privacy act as amended)", short: "Quebec 25", region: "Americas",
    jurisdiction: "Quebec, Canada", regulator: "CAI", color: "#1e3a8a",
    regulations: ["Quebec Law 25 (Act 25)"],
    refs: {
      principles: c("ss. 4–5 (necessity)"),
      lawful_basis: c("ss. 12, 14 (consent)", "legal-qc25-consent"),
      consent: c("ss. 12, 14", "legal-qc25-consent"),
      purpose: c("ss. 4–5, 12", "legal-qc25-consent"),
      retention: c("s. 23"),
      special_cat: c("s. 12 (express consent, sensitive)", "legal-qc25-consent"),
      children: c("s. 4.1 (under 14)"),
      transparency: c("ss. 8, 8.2"),
      access: c("s. 27"),
      erasure: c("s. 28.1 (de-indexing)"),
      other_rights: c("ss. 27–28 (portability, rectification)"),
      automated: c("s. 12.1"),
      cookies: c("s. 8.1 (tracking off by default)"),
      accountability: c("s. 3.2 (governance policies)", "legal-qc25-governor"),
      dpo: c("s. 3.1 (person in charge)", "legal-qc25-governor"),
      dpia: c("s. 3.3 (PIA)", "legal-qc25-pia"),
      by_design: c("s. 9.1 (privacy by default)"),
      processor: c("s. 18.3"),
      security: c("s. 10"),
      breach_regulator: c("s. 3.5 (serious injury)", "legal-qc25-breach"),
      breach_individuals: c("s. 3.5", "legal-qc25-breach"),
      transfers: c("s. 17 (PIA before transfer)", "legal-qc25-pia"),
      penalties: c("ss. 90.1–91 (C$25m / 4%)"),
    },
  },
  {
    id: "lgpd", label: "LGPD (Brazil)", short: "LGPD", region: "Americas",
    jurisdiction: "Brazil", regulator: "ANPD", color: "#15803d",
    regulations: ["LGPD (Lei Geral de Proteção de Dados)"],
    refs: {
      principles: c("Art. 6"),
      lawful_basis: c("Art. 7", "legal-lgpd-art7"),
      consent: c("Art. 8", "legal-lgpd-art8"),
      purpose: c("Art. 6(I)–(III)"),
      retention: c("Arts. 15–16", "legal-lgpd-art15"),
      special_cat: c("Art. 11", "legal-lgpd-art11"),
      children: c("Art. 14"),
      transparency: c("Art. 9"),
      access: c("Art. 18(II)", "legal-lgpd-art18"),
      erasure: c("Art. 18(IV), (VI)", "legal-lgpd-art18"),
      other_rights: c("Art. 18(III), (V), (VII)–(IX)", "legal-lgpd-art18"),
      automated: c("Art. 20"),
      cookies: c("ANPD cookie guide (2022)"),
      accountability: c("Art. 50", "legal-lgpd-art50"),
      dpo: c("Art. 41 (encarregado)", "legal-lgpd-art37"),
      ropa: c("Art. 37", "legal-lgpd-ropa"),
      dpia: c("Art. 38 (RIPD)"),
      by_design: c("Art. 46 §2", "legal-lgpd-art46"),
      processor: c("Arts. 39, 42"),
      security: c("Art. 46", "legal-lgpd-art46"),
      breach_regulator: c("Art. 48 (3 working days)", "legal-lgpd-art48"),
      breach_individuals: c("Art. 48", "legal-lgpd-art48"),
      transfers: c("Art. 33", "legal-lgpd-transfer"),
      penalties: c("Art. 52 (2% / R$50m)"),
    },
  },
  {
    id: "mx", label: "Mexico LFPDPPP (2025 re-enactment)", short: "LFPDPPP", region: "Americas",
    jurisdiction: "Mexico", regulator: "Secretaría Anticorrupción y Buen Gobierno (formerly INAI)", color: "#166534",
    regulations: ["Federal Law on Protection of Personal Data (LFPDPPP)"],
    refs: {
      principles: c("Principles (licitud, finalidad, proporcionalidad)"),
      consent: c("Consent (tacit / express)", "legal-mx-consent"),
      special_cat: c("Sensitive data (express written consent)", "legal-mx-consent"),
      transparency: c("Aviso de privacidad", "legal-mx-lfpdppp"),
      access: c("ARCO rights (access)", "legal-mx-lfpdppp"),
      erasure: c("ARCO rights (cancellation)", "legal-mx-lfpdppp"),
      other_rights: c("ARCO rights (rectification, opposition)", "legal-mx-lfpdppp"),
      dpo: c("Person or department for ARCO requests", "legal-mx-lfpdppp"),
      security: c("Security measures", "legal-mx-security"),
      breach_individuals: c("Breach notice to data subjects", "legal-mx-security"),
      transfers: c("Transfers (notice & consent)"),
      penalties: c("Sanctions (UMA-based fines)"),
    },
  },

  // ── Asia-Pacific ──────────────────────────────────────────────────────────
  {
    id: "au", label: "Australia Privacy Act 1988 (APPs & NDB scheme)", short: "APPs", region: "Asia-Pacific",
    jurisdiction: "Australia", regulator: "OAIC", color: "#ca8a04",
    regulations: ["Privacy Act 1988", "Australian Privacy Principles (APPs)", "Notifiable Data Breaches Scheme"],
    refs: {
      principles: c("APPs 1–13", "legal-au-apps"),
      lawful_basis: c("APP 3 (collection)", "legal-au-app3"),
      consent: c("APP 3.3, 6 (sensitive / secondary use)", "legal-au-app3"),
      purpose: c("APP 3, 6", "legal-au-app6"),
      retention: c("APP 11.2", "legal-au-app11"),
      special_cat: c("APP 3.3 (sensitive information)", "legal-au-app3"),
      children: c("Children's Online Privacy Code"),
      transparency: c("APP 1, 5", "legal-au-app1"),
      access: c("APP 12", "legal-au-app12-13"),
      other_rights: c("APP 13 (correction)", "legal-au-app12-13"),
      automated: c("APP 1.7–1.9 (from Dec 2026)"),
      marketing: c("APP 7 / Spam Act"),
      accountability: c("APP 1.2", "legal-au-app1"),
      by_design: c("APP 1.2", "legal-au-app1"),
      processor: c("APP 8 / contractual"),
      security: c("APP 11", "legal-au-app11"),
      breach_regulator: c("Pt IIIC (NDB, 30-day assessment)", "legal-au-ndb"),
      breach_individuals: c("s. 26WL", "legal-au-ndb"),
      transfers: c("APP 8", "legal-au-app8"),
      penalties: c("s. 13G (A$50m / 3× / 30%)"),
    },
  },
  {
    id: "nz", label: "New Zealand Privacy Act 2020", short: "NZ Privacy", region: "Asia-Pacific",
    jurisdiction: "New Zealand", regulator: "Privacy Commissioner", color: "#475569",
    regulations: ["Privacy Act 2020"],
    refs: {
      principles: c("IPPs 1–13 (s. 22)", "legal-nz-ipps"),
      lawful_basis: c("IPP 1 (lawful purpose)", "legal-nz-ipps"),
      consent: c("IPPs 3, 10–11 (authorisation)", "legal-nz-ipps"),
      purpose: c("IPPs 1, 10", "legal-nz-ipps"),
      retention: c("IPP 9", "legal-nz-ipps"),
      children: c("IPP 4(b)", "legal-nz-ipps"),
      transparency: c("IPP 3", "legal-nz-ipps"),
      access: c("IPP 6", "legal-nz-ipps"),
      other_rights: c("IPP 7 (correction)", "legal-nz-ipps"),
      marketing: c("Unsolicited Electronic Messages Act 2007"),
      dpo: c("s. 201 (privacy officer, mandatory)", "legal-nz-ipps"),
      processor: c("s. 11 (agents)"),
      security: c("IPP 5", "legal-nz-ipps"),
      breach_regulator: c("ss. 114–115 (serious harm, ASAP)", "legal-nz-breach"),
      breach_individuals: c("s. 115", "legal-nz-breach"),
      transfers: c("IPP 12", "legal-nz-transfer"),
      penalties: c("s. 212 (offences)"),
    },
  },
  {
    id: "sg", label: "Singapore PDPA 2012", short: "SG PDPA", region: "Asia-Pacific",
    jurisdiction: "Singapore", regulator: "PDPC", color: "#be123c",
    regulations: ["Personal Data Protection Act 2012 (PDPA)"],
    refs: {
      principles: c("11 obligations (Pts 3–6B)"),
      lawful_basis: c("s. 13 (consent / deemed consent)", "legal-pdpa-sg-consent"),
      consent: c("ss. 13–17", "legal-pdpa-sg-consent"),
      purpose: c("s. 18", "legal-pdpa-sg-purpose"),
      retention: c("s. 25", "legal-pdpa-sg-retention"),
      children: c("PDPC guidance (13+)"),
      transparency: c("s. 20", "legal-pdpa-sg-notify"),
      access: c("s. 21", "legal-pdpa-sg-access"),
      erasure: c("s. 16 (withdraw consent)", "legal-pdpa-sg-consent"),
      other_rights: c("s. 22 (correction), s. 26H (portability, not in force)", "legal-pdpa-sg-access"),
      marketing: c("DNC ss. 36–48", "legal-pdpa-sg-donotcall"),
      accountability: c("s. 12"),
      dpo: c("s. 11(3)", "legal-pdpa-sg-dpo"),
      processor: c("s. 4(2) (data intermediaries)"),
      security: c("s. 24", "legal-pdpa-sg-protect"),
      breach_regulator: c("s. 26D (3 calendar days)", "legal-pdpa-sg-breach"),
      breach_individuals: c("s. 26D(2)", "legal-pdpa-sg-breach"),
      transfers: c("s. 26", "legal-pdpa-sg-transfer"),
      penalties: c("s. 48J (10% / S$1m)"),
    },
  },
  {
    id: "my", label: "Malaysia PDPA 2010 (as amended 2024)", short: "MY PDPA", region: "Asia-Pacific",
    jurisdiction: "Malaysia", regulator: "Personal Data Protection Commissioner", color: "#0369a1",
    regulations: ["Personal Data Protection Act 2010 (PDPA)"],
    refs: {
      principles: c("s. 5 (7 principles)", "legal-my-pdpa"),
      lawful_basis: c("s. 6", "legal-my-consent"),
      consent: c("s. 6", "legal-my-consent"),
      purpose: c("s. 6(2)–(3)", "legal-my-pdpa"),
      retention: c("s. 10", "legal-my-pdpa"),
      special_cat: c("s. 40 (explicit consent)", "legal-my-consent"),
      transparency: c("s. 7 (notice & choice)", "legal-my-pdpa"),
      access: c("ss. 12, 30", "legal-my-pdpa"),
      other_rights: c("ss. 34, 43A (portability)", "legal-my-pdpa"),
      marketing: c("s. 43", "legal-my-pdpa"),
      dpo: c("s. 12A (2024 amendment)"),
      processor: c("s. 9(2)", "legal-my-pdpa"),
      registration: c("ss. 14–16 (prescribed data users)"),
      security: c("s. 9", "legal-my-pdpa"),
      breach_regulator: c("s. 12B (72 h)"),
      breach_individuals: c("s. 12B"),
      transfers: c("s. 129", "legal-my-transfer"),
      penalties: c("Offences (up to RM1m)"),
    },
  },
  {
    id: "th", label: "Thailand PDPA 2019", short: "TH PDPA", region: "Asia-Pacific",
    jurisdiction: "Thailand", regulator: "PDPC", color: "#7e22ce",
    regulations: ["Personal Data Protection Act 2019 (PDPA)"],
    refs: {
      principles: c("s. 22", "legal-th-pdpa"),
      lawful_basis: c("s. 24", "legal-th-pdpa"),
      consent: c("s. 19", "legal-th-pdpa"),
      purpose: c("ss. 21–22", "legal-th-pdpa"),
      retention: c("s. 37(3)", "legal-th-pdpa"),
      special_cat: c("s. 26", "legal-th-pdpa"),
      children: c("s. 20 (under 10 / 10–20)"),
      transparency: c("s. 23", "legal-th-pdpa"),
      access: c("s. 30"),
      erasure: c("s. 33"),
      other_rights: c("ss. 28, 31–35"),
      marketing: c("s. 32 (object)"),
      dpo: c("s. 41"),
      ropa: c("s. 39", "legal-th-ropa"),
      processor: c("s. 40"),
      representative: c("s. 37(5)"),
      security: c("s. 37(1)", "legal-th-pdpa"),
      breach_regulator: c("s. 37(4) (72 h)", "legal-th-breach"),
      breach_individuals: c("s. 37(4)", "legal-th-breach"),
      transfers: c("ss. 28–29"),
      penalties: c("ss. 82–90 (THB 5m)"),
    },
  },
  {
    id: "ph", label: "Philippines Data Privacy Act 2012", short: "PH DPA", region: "Asia-Pacific",
    jurisdiction: "Philippines", regulator: "NPC", color: "#0e7490",
    regulations: ["Data Privacy Act of 2012 (DPA)"],
    refs: {
      principles: c("s. 11", "legal-ph-dpa"),
      lawful_basis: c("ss. 12–13", "legal-ph-dpa"),
      consent: c("ss. 3(b), 12(a)", "legal-ph-dpa"),
      purpose: c("s. 11", "legal-ph-dpa"),
      retention: c("s. 11(e)", "legal-ph-dpa"),
      special_cat: c("s. 13 (sensitive PI)", "legal-ph-dpa"),
      transparency: c("s. 16(a)–(b)", "legal-ph-rights"),
      access: c("s. 16(c)", "legal-ph-rights"),
      erasure: c("s. 16(e)", "legal-ph-rights"),
      other_rights: c("ss. 16(d), (f), 18", "legal-ph-rights"),
      accountability: c("s. 21"),
      dpo: c("NPC Advisory 2017-01"),
      ropa: c("IRR s. 26(b)", "legal-ph-ropa"),
      dpia: c("NPC Advisory 2017-03 (PIA)"),
      processor: c("s. 14"),
      registration: c("NPC Circular 2022-04"),
      security: c("s. 20"),
      breach_regulator: c("s. 20(f) (72 h)", "legal-ph-breach"),
      breach_individuals: c("s. 20(f)", "legal-ph-breach"),
      transfers: c("s. 21"),
      penalties: c("ss. 25–36 (criminal)"),
    },
  },
  {
    id: "hk", label: "Hong Kong PDPO", short: "HK PDPO", region: "Asia-Pacific",
    jurisdiction: "Hong Kong SAR", regulator: "PCPD", color: "#b91c1c",
    regulations: ["Personal Data (Privacy) Ordinance (PDPO)"],
    refs: {
      principles: c("DPPs 1–6 (Sch. 1)", "legal-hk-pdpo"),
      lawful_basis: c("DPP 1", "legal-hk-pdpo"),
      consent: c("DPP 3 (prescribed consent)", "legal-hk-pdpo"),
      purpose: c("DPPs 1, 3", "legal-hk-pdpo"),
      retention: c("DPP 2(2), s. 26", "legal-hk-pdpo"),
      transparency: c("DPP 1(3), DPP 5", "legal-hk-pdpo"),
      access: c("DPP 6, ss. 18–28", "legal-hk-pdpo"),
      erasure: c("s. 26", "legal-hk-pdpo"),
      other_rights: c("DPP 6, ss. 22–25 (correction)", "legal-hk-pdpo"),
      marketing: c("Pt 6A (ss. 35A–35M)", "legal-hk-dnotices"),
      processor: c("DPP 2(3), 4(2)", "legal-hk-pdpo"),
      security: c("DPP 4", "legal-hk-pdpo"),
      breach_regulator: c("Voluntary (PCPD guidance)"),
      breach_individuals: c("Voluntary (PCPD guidance)"),
      transfers: c("s. 33 (not in force) / guidance", "legal-hk-transfer"),
      penalties: c("ss. 50A, 64"),
    },
  },
  {
    id: "tw", label: "Taiwan PDPA", short: "TW PDPA", region: "Asia-Pacific",
    jurisdiction: "Taiwan", regulator: "Personal Data Protection Commission", color: "#0d9488",
    regulations: ["Personal Data Protection Act (PDPA)"],
    refs: {
      principles: c("Art. 5", "legal-tw-pdpa"),
      lawful_basis: c("Art. 19", "legal-tw-pdpa"),
      consent: c("Art. 7", "legal-tw-pdpa"),
      purpose: c("Arts. 5, 20", "legal-tw-pdpa"),
      retention: c("Art. 11(3)", "legal-tw-pdpa"),
      special_cat: c("Art. 6", "legal-tw-pdpa"),
      transparency: c("Arts. 8–9", "legal-tw-pdpa"),
      access: c("Arts. 3, 10", "legal-tw-pdpa"),
      erasure: c("Arts. 3, 11", "legal-tw-pdpa"),
      other_rights: c("Arts. 3, 11", "legal-tw-pdpa"),
      marketing: c("Art. 20(2)–(3) (opt-out)", "legal-tw-pdpa"),
      dpo: c("Art. 27 (designated sectors)"),
      security: c("Art. 27", "legal-tw-pdpa"),
      breach_individuals: c("Art. 12", "legal-tw-pdpa"),
      transfers: c("Art. 21", "legal-tw-pdpa"),
      penalties: c("Arts. 41–50"),
    },
  },
  {
    id: "vn", label: "Vietnam Personal Data Protection Law 2025 (formerly Decree 13/2023)", short: "VN PDPL", region: "Asia-Pacific",
    jurisdiction: "Vietnam", regulator: "Ministry of Public Security (A05)", color: "#c2410c",
    regulations: ["Law on Personal Data Protection 2025 (Law 91/2025/QH15)", "Decree 13/2023/ND-CP on Personal Data Protection"],
    refs: {
      principles: c("Principles (D13 Art. 3)", "legal-vn-dec13"),
      lawful_basis: c("Consent + exceptions (D13 Arts. 11, 17)", "legal-vn-dec13"),
      consent: c("Consent (D13 Art. 11)", "legal-vn-dec13"),
      purpose: c("Principles (D13 Art. 3)", "legal-vn-dec13"),
      retention: c("Principles (D13 Art. 3)", "legal-vn-dec13"),
      special_cat: c("Sensitive data (D13 Art. 2(4))", "legal-vn-dec13"),
      children: c("Children (D13 Art. 20, under 7 / 7+)"),
      transparency: c("Notice (D13 Art. 13)", "legal-vn-dec13"),
      access: c("Rights (D13 Arts. 9, 14)", "legal-vn-dec13"),
      erasure: c("Rights (D13 Arts. 9, 16)", "legal-vn-dec13"),
      other_rights: c("Rights (D13 Art. 9)", "legal-vn-dec13"),
      dpo: c("DPO for sensitive data (D13 Art. 28)"),
      dpia: c("Assessment dossier filed with MPS (D13 Art. 24)"),
      processor: c("Processor duties (D13 Art. 39)"),
      security: c("Security (D13 Arts. 26–27)"),
      breach_regulator: c("72 h to MPS (D13 Art. 23)"),
      transfers: c("Transfer dossier (D13 Art. 25)", "legal-vn-transfer"),
      penalties: c("PDPL 2025 (up to 5% revenue)"),
    },
  },
  {
    id: "in", label: "India Digital Personal Data Protection Act 2023", short: "DPDP", region: "Asia-Pacific",
    jurisdiction: "India", regulator: "Data Protection Board of India", color: "#ea580c",
    regulations: ["Digital Personal Data Protection Act 2023 (DPDP)"],
    refs: {
      principles: c("s. 4 (lawful purpose)", "legal-dpdp-s4"),
      lawful_basis: c("s. 4 (consent / legitimate uses s. 7)", "legal-dpdp-s4"),
      consent: c("s. 6", "legal-dpdp-s6"),
      purpose: c("ss. 6(1), 8(7)", "legal-dpdp-s8"),
      retention: c("s. 8(7)", "legal-dpdp-s8"),
      children: c("s. 9 (under 18)", "legal-dpdp-s9"),
      transparency: c("s. 5 (notice)"),
      access: c("s. 11", "legal-dpdp-rights"),
      erasure: c("s. 12", "legal-dpdp-rights"),
      other_rights: c("ss. 12–14", "legal-dpdp-rights"),
      accountability: c("s. 8", "legal-dpdp-s8"),
      dpo: c("s. 10(2)(a) (SDF DPO in India)", "legal-dpdp-s16"),
      dpia: c("s. 10(2)(c) (SDF DPIA)", "legal-dpdp-s16"),
      processor: c("s. 8(2)", "legal-dpdp-processor"),
      security: c("s. 8(5)", "legal-dpdp-s8"),
      breach_regulator: c("s. 8(6) / Rules (72 h report)", "legal-dpdp-s8"),
      breach_individuals: c("s. 8(6)", "legal-dpdp-s8"),
      transfers: c("s. 16", "legal-dpdp-transfer"),
      penalties: c("s. 33 / Schedule (INR 250 crore)", "legal-dpdp-board"),
    },
  },
  {
    id: "cn", label: "China PIPL", short: "PIPL", region: "Asia-Pacific",
    jurisdiction: "People's Republic of China", regulator: "CAC", color: "#dc2626",
    regulations: ["Personal Information Protection Law (PIPL)"],
    refs: {
      principles: c("Arts. 5–9"),
      lawful_basis: c("Art. 13", "legal-pipl-art13"),
      consent: c("Arts. 14–16", "legal-pipl-art14"),
      purpose: c("Art. 6"),
      retention: c("Art. 19"),
      special_cat: c("Arts. 28–32", "legal-pipl-art28"),
      children: c("Art. 31 (under 14)", "legal-pipl-art28"),
      transparency: c("Art. 17", "legal-pipl-art17"),
      access: c("Art. 45", "legal-pipl-art44"),
      erasure: c("Art. 47", "legal-pipl-art44"),
      other_rights: c("Arts. 44–46", "legal-pipl-art44"),
      automated: c("Art. 24"),
      marketing: c("Art. 24(2) (targeted push opt-out)"),
      accountability: c("Art. 51", "legal-pipl-art51"),
      dpo: c("Art. 52 (threshold per CAC measures)"),
      dpia: c("Arts. 55–56 (PIPIA)", "legal-pipl-art55"),
      processor: c("Arts. 21–23", "legal-pipl-art22"),
      registration: c("Art. 53 / CBDT filings"),
      representative: c("Art. 53"),
      security: c("Art. 51", "legal-pipl-art51"),
      breach_regulator: c("Art. 57 (immediately)", "legal-pipl-art57"),
      breach_individuals: c("Art. 57", "legal-pipl-art57"),
      transfers: c("Arts. 38–40", "legal-pipl-art38"),
      penalties: c("Art. 66 (RMB 50m / 5%)"),
    },
  },
  {
    id: "jp", label: "Japan APPI", short: "APPI", region: "Asia-Pacific",
    jurisdiction: "Japan", regulator: "PPC", color: "#db2777",
    regulations: ["Act on the Protection of Personal Information (APPI)"],
    refs: {
      principles: c("Arts. 17–19", "legal-appi-purpose"),
      lawful_basis: c("Art. 18 (use within purpose)", "legal-appi-purpose"),
      consent: c("Arts. 18(1), 27", "legal-appi-purpose"),
      purpose: c("Arts. 17–18", "legal-appi-purpose"),
      retention: c("Art. 22", "legal-appi-retention"),
      special_cat: c("Arts. 2(3), 20(2)", "legal-appi-sensitive"),
      children: c("PPC guidance"),
      transparency: c("Art. 21", "legal-appi-acquire"),
      access: c("Art. 33", "legal-appi-rights"),
      erasure: c("Art. 35", "legal-appi-rights"),
      other_rights: c("Arts. 34–35", "legal-appi-rights"),
      marketing: c("Art. 27(2) (opt-out scheme)"),
      cookies: c("Art. 31 (personally referable information)"),
      ropa: c("Arts. 29–30 (third-party provision records)", "legal-appi-thirdparty"),
      processor: c("Art. 25 (supervision of trustees)"),
      security: c("Art. 23", "legal-appi-security"),
      breach_regulator: c("Art. 26 (prompt, then 30/60 days)", "legal-appi-breach"),
      breach_individuals: c("Art. 26(2)", "legal-appi-breach"),
      transfers: c("Art. 28", "legal-appi-thirdparty"),
      penalties: c("Arts. 178–185 (JPY 100m)"),
    },
  },
  {
    id: "kr", label: "South Korea PIPA", short: "PIPA", region: "Asia-Pacific",
    jurisdiction: "South Korea", regulator: "PIPC", color: "#0891b2",
    regulations: ["Personal Information Protection Act (PIPA)"],
    refs: {
      principles: c("Art. 3"),
      lawful_basis: c("Art. 15", "legal-pipa-kr-art15"),
      consent: c("Art. 22", "legal-pipa-kr-art22"),
      purpose: c("Arts. 3, 18", "legal-pipa-kr-art15"),
      retention: c("Art. 21 (destroy within 5 days)", "legal-pipa-kr-art21"),
      special_cat: c("Arts. 23–24"),
      children: c("Art. 22-2 (under 14)", "legal-pipa-kr-art22"),
      transparency: c("Arts. 15(2), 30"),
      access: c("Art. 35", "legal-pipa-kr-art35"),
      erasure: c("Art. 36", "legal-pipa-kr-art35"),
      other_rights: c("Arts. 35-2, 36–37", "legal-pipa-kr-art35"),
      automated: c("Art. 37-2"),
      marketing: c("Art. 22(4) / Network Act"),
      accountability: c("Arts. 29–31", "legal-pipa-kr-art29"),
      dpo: c("Art. 31 (CPO, mandatory)", "legal-pipa-kr-cpo"),
      dpia: c("Art. 33 (public institutions)"),
      processor: c("Art. 26 (outsourcing)"),
      representative: c("Art. 31-2 (domestic agent)"),
      security: c("Art. 29", "legal-pipa-kr-art29"),
      breach_regulator: c("Art. 34 (72 h)", "legal-pipa-kr-art34"),
      breach_individuals: c("Art. 34(1)", "legal-pipa-kr-art34"),
      transfers: c("Art. 28-8", "legal-pipa-kr-transfer"),
      penalties: c("Art. 64-2 (3% of revenue)"),
    },
  },

  // ── Middle East & Africa ──────────────────────────────────────────────────
  {
    id: "za", label: "South Africa POPIA", short: "POPIA", region: "Middle East & Africa",
    jurisdiction: "South Africa", regulator: "Information Regulator", color: "#16a34a",
    regulations: ["Protection of Personal Information Act (POPIA)"],
    refs: {
      principles: c("s. 4 (8 conditions)", "legal-popia-s8"),
      lawful_basis: c("s. 11", "legal-popia-s9"),
      consent: c("s. 11(1)(a), (2)–(3)", "legal-popia-s9"),
      purpose: c("ss. 13–15", "legal-popia-s13"),
      retention: c("s. 14", "legal-popia-s13"),
      special_cat: c("ss. 26–33"),
      children: c("ss. 34–35"),
      transparency: c("s. 18", "legal-popia-s18"),
      access: c("s. 23", "legal-popia-s23"),
      erasure: c("s. 24", "legal-popia-s23"),
      other_rights: c("ss. 11(3), 24", "legal-popia-s23"),
      automated: c("s. 71"),
      marketing: c("s. 69"),
      accountability: c("s. 8", "legal-popia-s8"),
      dpo: c("ss. 55–56 (Information Officer)", "legal-popia-s55"),
      ropa: c("s. 17 / PAIA manual", "legal-popia-s17"),
      dpia: c("Reg. 4(1)(b)"),
      processor: c("ss. 20–21 (operators)"),
      registration: c("s. 55(2)", "legal-popia-s55"),
      security: c("s. 19", "legal-popia-s19"),
      breach_regulator: c("s. 22 (as soon as reasonably possible)", "legal-popia-s22"),
      breach_individuals: c("s. 22", "legal-popia-s22"),
      transfers: c("s. 72"),
      penalties: c("ss. 107–109 (R10m / criminal)"),
    },
  },
  {
    id: "tr", label: "Turkey KVKK (Law No. 6698)", short: "KVKK", region: "Middle East & Africa",
    jurisdiction: "Turkey", regulator: "KVKK Board", color: "#dc2626",
    regulations: ["Personal Data Protection Law No. 6698 (KVKK)"],
    refs: {
      principles: c("Art. 4", "legal-kvkk-art5"),
      lawful_basis: c("Art. 5", "legal-kvkk-art5"),
      consent: c("Arts. 3(1)(a), 5(1) (explicit)", "legal-kvkk-art5"),
      purpose: c("Art. 4(2)", "legal-kvkk-art5"),
      retention: c("Art. 7"),
      special_cat: c("Art. 6", "legal-kvkk-art5"),
      transparency: c("Art. 10", "legal-kvkk-art10"),
      access: c("Art. 11", "legal-kvkk-art11"),
      erasure: c("Arts. 7, 11(e)", "legal-kvkk-art11"),
      other_rights: c("Art. 11", "legal-kvkk-art11"),
      automated: c("Art. 11(g)", "legal-kvkk-art11"),
      marketing: c("Law 6563 (commercial messages, İYS)"),
      cookies: c("KVKK Cookie Guideline (2022)"),
      dpo: c("Contact person (VERBİS)", "legal-kvkk-dpo"),
      ropa: c("Processing inventory (VERBİS Reg.)", "legal-kvkk-verbis"),
      processor: c("Arts. 8, 12(2)", "legal-kvkk-art12"),
      registration: c("Art. 16 (VERBİS)", "legal-kvkk-verbis"),
      representative: c("VERBİS Reg. Art. 11", "legal-kvkk-verbis"),
      security: c("Art. 12", "legal-kvkk-art12"),
      breach_regulator: c("Art. 12(5) (72 h, Board decision 2019/10)", "legal-kvkk-art12"),
      breach_individuals: c("Art. 12(5)", "legal-kvkk-art12"),
      transfers: c("Arts. 8–9", "legal-kvkk-art8"),
      penalties: c("Art. 18"),
    },
  },
  {
    id: "il", label: "Israel Privacy Protection Law 5741-1981 (Amendment 13)", short: "IL PPL", region: "Middle East & Africa",
    jurisdiction: "Israel", regulator: "Privacy Protection Authority", color: "#2563eb",
    regulations: ["Privacy Protection Law"],
    refs: {
      principles: c("ss. 2, 8", "legal-il-privacy"),
      lawful_basis: c("ss. 1, 8 (informed consent)", "legal-il-privacy"),
      consent: c("s. 3", "legal-il-privacy"),
      purpose: c("ss. 2(9), 8(b)", "legal-il-privacy"),
      special_cat: c("s. 7 (sensitive information)", "legal-il-privacy"),
      transparency: c("s. 11", "legal-il-privacy"),
      access: c("s. 13", "legal-il-privacy"),
      erasure: c("s. 14", "legal-il-privacy"),
      other_rights: c("s. 14 (correction)", "legal-il-privacy"),
      marketing: c("s. 17F (direct mailing)"),
      accountability: c("Data Security Regs 2017"),
      dpo: c("Amendment 13 (mandatory DPO)"),
      ropa: c("Data Security Regs r. 2 (database document)", "legal-il-ropa"),
      dpia: c("Data Security Regs (risk survey)"),
      processor: c("Data Security Regs r. 15 (outsourcing)"),
      registration: c("s. 8 (database registration)", "legal-il-privacy"),
      security: c("s. 17 / Data Security Regs 2017", "legal-il-privacy"),
      breach_regulator: c("Data Security Regs r. 11(d) (severe incidents)"),
      breach_individuals: c("r. 11(d) (as directed by PPA)"),
      transfers: c("Transfer Regulations 2001"),
      penalties: c("Amendment 13 (administrative fines)"),
    },
  },
  {
    id: "uae", label: "UAE Federal PDPL (Decree-Law 45/2021)", short: "UAE PDPL", region: "Middle East & Africa",
    jurisdiction: "United Arab Emirates (onshore)", regulator: "UAE Data Office", color: "#059669",
    regulations: ["Federal Personal Data Protection Law (PDPL)"],
    refs: {
      principles: c("Art. 5", "legal-uae-pdpl"),
      lawful_basis: c("Art. 4 (consent + exceptions)", "legal-uae-pdpl"),
      consent: c("Art. 6", "legal-uae-pdpl"),
      purpose: c("Art. 5", "legal-uae-pdpl"),
      retention: c("Art. 5", "legal-uae-pdpl"),
      special_cat: c("Art. 1 (sensitive data), Art. 10", "legal-uae-pdpl"),
      transparency: c("Art. 13", "legal-uae-pdpl"),
      access: c("Art. 13", "legal-uae-pdpl"),
      erasure: c("Art. 15", "legal-uae-pdpl"),
      other_rights: c("Arts. 14–18", "legal-uae-pdpl"),
      automated: c("Art. 18", "legal-uae-pdpl"),
      marketing: c("Art. 17 (object to direct marketing)", "legal-uae-pdpl"),
      dpo: c("Art. 10"),
      ropa: c("Art. 7(2) (record of processing)", "legal-uae-ropa"),
      dpia: c("Art. 21"),
      processor: c("Arts. 7(3), 8"),
      security: c("Art. 20", "legal-uae-pdpl"),
      breach_regulator: c("Art. 9 (Data Office)", "legal-uae-breach"),
      breach_individuals: c("Art. 9", "legal-uae-breach"),
      transfers: c("Arts. 22–23"),
    },
  },
  {
    id: "difc", label: "DIFC Data Protection Law No. 5 of 2020", short: "DIFC DPL", region: "Middle East & Africa",
    jurisdiction: "Dubai International Financial Centre", regulator: "DIFC Commissioner of Data Protection", color: "#047857",
    regulations: ["DIFC Data Protection Law"],
    refs: {
      principles: c("Art. 9", "legal-difc-dpl"),
      lawful_basis: c("Art. 10", "legal-difc-dpl"),
      consent: c("Art. 12", "legal-difc-dpl"),
      purpose: c("Art. 9(1)(b)–(c)", "legal-difc-dpl"),
      retention: c("Art. 9(1)(e)", "legal-difc-dpl"),
      special_cat: c("Art. 11", "legal-difc-dpl"),
      transparency: c("Arts. 29–30", "legal-difc-dpl"),
      access: c("Art. 33", "legal-difc-dpl"),
      erasure: c("Art. 33", "legal-difc-dpl"),
      other_rights: c("Arts. 33–34, 38", "legal-difc-dpl"),
      automated: c("Art. 38", "legal-difc-dpl"),
      marketing: c("Art. 34 (object)", "legal-difc-dpl"),
      accountability: c("Art. 14", "legal-difc-dpl"),
      dpo: c("Arts. 16–19", "legal-difc-dpl"),
      ropa: c("Art. 15", "legal-difc-ropa"),
      dpia: c("Art. 20", "legal-difc-dpl"),
      by_design: c("Art. 14", "legal-difc-dpl"),
      processor: c("Art. 24", "legal-difc-dpl"),
      registration: c("Art. 14(7) (notification & fee)", "legal-difc-dpl"),
      security: c("Art. 14", "legal-difc-dpl"),
      breach_regulator: c("Art. 41 (as soon as practicable)", "legal-difc-dpl"),
      breach_individuals: c("Art. 41", "legal-difc-dpl"),
      transfers: c("Arts. 26–27", "legal-difc-transfer"),
      penalties: c("Art. 62 / Sch. 2"),
    },
  },
  {
    id: "adgm", label: "ADGM Data Protection Regulations 2021", short: "ADGM DPR", region: "Middle East & Africa",
    jurisdiction: "Abu Dhabi Global Market", regulator: "ADGM Office of Data Protection", color: "#065f46",
    regulations: ["ADGM Data Protection Regulations"],
    refs: {
      principles: c("Principles", "legal-adgm-dp"),
      lawful_basis: c("Lawful basis", "legal-adgm-dp"),
      consent: c("Consent", "legal-adgm-dp"),
      purpose: c("Principles", "legal-adgm-dp"),
      retention: c("Principles", "legal-adgm-dp"),
      special_cat: c("Special categories", "legal-adgm-dp"),
      transparency: c("Notice", "legal-adgm-dp"),
      access: c("Access", "legal-adgm-dp"),
      erasure: c("Erasure", "legal-adgm-dp"),
      other_rights: c("Rights", "legal-adgm-dp"),
      automated: c("Automated decisions", "legal-adgm-dp"),
      dpo: c("DPO", "legal-adgm-dp"),
      ropa: c("s. 13", "legal-adgm-ropa"),
      dpia: c("DPIA", "legal-adgm-dp"),
      processor: c("Processor terms", "legal-adgm-dp"),
      registration: c("Notification & fee", "legal-adgm-dp"),
      security: c("Security", "legal-adgm-dp"),
      breach_regulator: c("Breach (72 h)", "legal-adgm-dp"),
      breach_individuals: c("Breach", "legal-adgm-dp"),
      transfers: c("Transfers", "legal-adgm-dp"),
      penalties: c("Fines (up to US$28m)"),
    },
  },
  {
    id: "sa", label: "Saudi Arabia PDPL", short: "SA PDPL", region: "Middle East & Africa",
    jurisdiction: "Saudi Arabia", regulator: "SDAIA", color: "#15803d",
    regulations: ["Personal Data Protection Law (Saudi Arabia)"],
    refs: {
      principles: c("Arts. 11–12", "legal-sa-pdpl"),
      lawful_basis: c("Arts. 5–6 (consent + exceptions)", "legal-sa-pdpl"),
      consent: c("Arts. 5, 7", "legal-sa-pdpl"),
      purpose: c("Art. 11", "legal-sa-pdpl"),
      retention: c("Art. 18", "legal-sa-pdpl"),
      special_cat: c("Sensitive data (Arts. 1, 7)", "legal-sa-pdpl"),
      children: c("Implementing Regulations (parental consent)"),
      transparency: c("Art. 12", "legal-sa-pdpl"),
      access: c("Art. 4(2)–(3)", "legal-sa-pdpl"),
      erasure: c("Arts. 4(5), 18", "legal-sa-pdpl"),
      other_rights: c("Art. 4", "legal-sa-pdpl"),
      marketing: c("Arts. 25–26", "legal-sa-pdpl"),
      accountability: c("Art. 30 (policies)", "legal-sa-pdpl"),
      dpo: c("Art. 30(2) / Regulations"),
      ropa: c("Art. 31 (records)", "legal-sa-ropa"),
      dpia: c("Art. 22"),
      processor: c("Art. 8"),
      security: c("Art. 19", "legal-sa-pdpl"),
      breach_regulator: c("Art. 20 (72 h per Regulations)"),
      breach_individuals: c("Art. 20"),
      transfers: c("Art. 29", "legal-sa-transfer"),
      penalties: c("Arts. 35–36 (SAR 5m / criminal)"),
    },
  },
  {
    id: "om", label: "Oman PDPL (Royal Decree 6/2022)", short: "OM PDPL", region: "Middle East & Africa",
    jurisdiction: "Oman", regulator: "MTCIT", color: "#b45309",
    regulations: ["Personal Data Protection Law (Oman, Royal Decree 6/2022)"],
    refs: {
      principles: c("Core obligations", "legal-om-pdpl"),
      lawful_basis: c("Consent (explicit)", "legal-om-pdpl"),
      consent: c("Consent (explicit)", "legal-om-pdpl"),
      special_cat: c("Sensitive data (Ministry permit)", "legal-om-pdpl"),
      transparency: c("Notice", "legal-om-pdpl"),
      access: c("Rights", "legal-om-pdpl"),
      erasure: c("Rights", "legal-om-pdpl"),
      other_rights: c("Rights", "legal-om-pdpl"),
      dpo: c("Executive Regulations (DPO)"),
      security: c("Security", "legal-om-pdpl"),
      breach_regulator: c("Breach (72 h, Executive Regulations)"),
      breach_individuals: c("Breach"),
      transfers: c("Transfers", "legal-om-pdpl"),
      penalties: c("Fines (up to OMR 500k)"),
    },
  },
  {
    id: "qa", label: "Qatar PDPL (Law No. 13 of 2016)", short: "QA PDPL", region: "Middle East & Africa",
    jurisdiction: "Qatar", regulator: "National Cyber Security Agency", color: "#7c2d12",
    regulations: ["Personal Data Protection Law No. 13 of 2016"],
    refs: {
      principles: c("Art. 3", "legal-qa-pdpl"),
      lawful_basis: c("Art. 4 (consent)", "legal-qa-pdpl"),
      consent: c("Art. 4", "legal-qa-pdpl"),
      special_cat: c("Art. 16 (special-nature data permit)", "legal-qa-pdpl"),
      transparency: c("Notice", "legal-qa-pdpl"),
      access: c("Rights", "legal-qa-pdpl"),
      erasure: c("Rights", "legal-qa-pdpl"),
      other_rights: c("Rights", "legal-qa-pdpl"),
      marketing: c("Arts. 21–23 (direct marketing)", "legal-qa-pdpl"),
      security: c("Security", "legal-qa-pdpl"),
      breach_regulator: c("Art. 14 (72 h per guidelines)", "legal-qa-pdpl"),
      breach_individuals: c("Art. 14", "legal-qa-pdpl"),
      transfers: c("Art. 15", "legal-qa-pdpl"),
      penalties: c("Arts. 24–26 (QAR 5m)"),
    },
  },
]);

const REGIONS: Region[] = ["UK & Crown Dependencies", "Europe", "Americas", "Asia-Pacific", "Middle East & Africa"];
const CATEGORIES = [...new Set(TOPICS.map((t) => t.category))];
const LAW_BY_ID = new Map(LAWS.map((l) => [l.id, l]));

// Seeded obligations a law points at from more than one row. One register entry then stands for several
// different duties (Art. 5 principles for consent and accountability alike), so its status is not shown
// against any single row and it never counts as evidence reuse.
const SHARED_CONTROLS = new Map<string, Set<string>>(LAWS.map((law) => {
  const uses = new Map<string, number>();
  for (const cell of Object.values(law.refs)) {
    if (cell?.controlId) uses.set(cell.controlId, (uses.get(cell.controlId) ?? 0) + 1);
  }
  return [law.id, new Set([...uses].filter(([, n]) => n > 1).map(([id]) => id))];
}));

const isSharedCell = (law: DpLaw, cell: Cell | undefined): boolean =>
  !!cell?.controlId && (SHARED_CONTROLS.get(law.id)?.has(cell.controlId) ?? false);

const rowsSharing = (law: DpLaw, controlId: string): string[] =>
  TOPICS.filter((t) => law.refs[t.key]?.controlId === controlId).map((t) => t.topic.toLowerCase());

const changeLine = (cell: Cell): string | null =>
  cell.inForce ? `In force since ${cell.inForce}${cell.replaced ? `; replaced ${cell.replaced}` : ""}` : null;

// Legal Obligations status for a seeded control, plus "EXEMPT" when the
// data-protection threshold engine (or an explicit override) says the
// obligation does not apply to this organisation.
type CellStatus = "COMPLIANT" | "IN_PROGRESS" | "GAP" | "NOT_STARTED" | "EXEMPT";

const STATUS_DOT: Record<CellStatus, { bg: string; title: string }> = {
  // Status labels as the Legal obligations page and every standard page word them.
  COMPLIANT:   { bg: "var(--c-success)", title: "Compliant" },
  IN_PROGRESS: { bg: "var(--c-warning)", title: "In progress" },
  GAP:         { bg: "var(--c-danger)", title: "Gap identified" },
  NOT_STARTED: { bg: "var(--c-border)", title: "Not assessed" },
  EXEMPT:      { bg: "transparent", title: "Not applicable — exempt by threshold" },
};

interface LawCounts { compliant: number; inProgress: number; gap: number; notStarted: number; exempt: number; total: number }

// Shape of GET /legal/soa. `applicable` / `applicability` arrive with the threshold
// engine; older API builds omit them, in which case every obligation applies.
interface LegalSoaRow {
  control: { id: string; domain: string | null; name: string };
  entry: { status: string; applicable: boolean | null };
  applicable?: boolean;
  applicability?: { status: string; headline: string } | null;
}

interface ObligationState { status: CellStatus; name: string; domain: string | null; headline: string | null }

export function DataProtectionMappingPage() {
  // Inside the consultant's client portal, navigation is page state rather than a URL.
  const navigate = usePortalNavigate();

  const [regulations, setRegulations] = useState<Set<string>>(new Set());
  const [dpTier, setDpTier] = useState<string | null>(null);
  const [obligations, setObligations] = useState<Map<string, ObligationState>>(new Map());
  const [loaded, setLoaded] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState<"all" | Region>("all");
  const [applicableOnly, setApplicableOnly] = useState(true);
  const [dimOthers, setDimOthers] = useState(true);
  const [expandedTopic, setExpandedTopic] = useState<TopicKey | null>(null);
  const [activeTab, setActiveTab] = useState<"table" | "reuse">("table");

  useEffect(() => {
    api.get<{ soa: LegalSoaRow[]; regulations: string[] }>("/legal/soa")
      .then(({ soa, regulations: regs }) => {
        const map = new Map<string, ObligationState>();
        for (const row of soa) {
          const applies = row.applicable ?? true;
          const raw = row.entry.status;
          const status: CellStatus = !applies ? "EXEMPT"
            : raw === "COMPLIANT" || raw === "IN_PROGRESS" || raw === "GAP" ? raw
            : "NOT_STARTED";
          map.set(row.control.id, { status, name: row.control.name, domain: row.control.domain, headline: row.applicability?.headline ?? null });
        }
        setObligations(map);
        setRegulations(new Set(regs));
      })
      .catch(() => {})
      .finally(() => setLoaded(true));

    api.get<{ organization: { dpTier: string | null } }>("/organizations/profile")
      .then(({ organization }) => {
        setDpTier(organization.dpTier ?? null);
      })
      .catch(() => {});
  }, []);

  // A law applies when one of its regulation strings is in the org's derived regulations — and not every
  // one of its obligations is exempt or marked not applicable (as backend lawsInEffect decides it): GLBA
  // for an organisation that is not a financial institution is listed, but does not apply.
  const allExempt = (law: DpLaw): boolean => {
    let seen = false;
    for (const ob of obligations.values()) {
      if (!ob.domain || !law.regulations.includes(ob.domain)) continue;
      seen = true;
      if (ob.status !== "EXEMPT") return false;
    }
    return seen;
  };
  const isApplicable = (law: DpLaw): boolean => law.regulations.some((r) => regulations.has(r)) && !allExempt(law);

  const applicableLaws = LAWS.filter(isApplicable);
  const showApplicableOnly = applicableOnly && applicableLaws.length > 0;
  const visibleLaws = showApplicableOnly
    ? applicableLaws
    : LAWS.filter((l) => regionFilter === "all" || l.region === regionFilter);
  const visibleRegions = REGIONS.filter((r) => visibleLaws.some((l) => l.region === r));

  const cellStatus = (law: DpLaw, cell: Cell | undefined): ObligationState | null => {
    if (!cell?.controlId || !isApplicable(law)) return null;
    return obligations.get(cell.controlId) ?? null;
  };

  // Per-law obligation counts for the summary cards (applicable laws only).
  // Mirrors summariseLegalSoa: exempt obligations sit outside the denominator.
  const countsFor = (law: DpLaw): LawCounts | null => {
    const counts: LawCounts = { compliant: 0, inProgress: 0, gap: 0, notStarted: 0, exempt: 0, total: 0 };
    let seen = false;
    for (const ob of obligations.values()) {
      if (!ob.domain || !law.regulations.includes(ob.domain)) continue;
      seen = true;
      if (ob.status === "EXEMPT") { counts.exempt++; continue; }
      counts.total++;
      if (ob.status === "COMPLIANT") counts.compliant++;
      else if (ob.status === "IN_PROGRESS") counts.inProgress++;
      else if (ob.status === "GAP") counts.gap++;
      else counts.notStarted++;
    }
    return seen ? counts : null;
  };

  // Evidence reuse: the same obligation is COMPLIANT under one applicable law and
  // GAP / NOT_STARTED under another. Cells without a seeded obligation carry no
  // status and cannot take part; exempt obligations are not gaps; a shared
  // obligation (one entry for several rows) says nothing about this row alone.
  const reuseOpportunities = TOPICS.flatMap((topic) => {
    const withStatus = applicableLaws
      .map((law) => ({ law, cell: law.refs[topic.key], state: cellStatus(law, law.refs[topic.key]) }))
      .filter((x): x is { law: DpLaw; cell: Cell; state: ObligationState } => !!x.cell && !!x.state && !isSharedCell(x.law, x.cell));
    if (withStatus.length < 2) return [];
    const compliant = withStatus.filter((x) => x.state.status === "COMPLIANT");
    const gaps = withStatus.filter((x) => x.state.status === "GAP" || x.state.status === "NOT_STARTED");
    if (compliant.length === 0 || gaps.length === 0) return [];
    return [{ topic, compliant, gaps }];
  });

  const searchLower = search.toLowerCase();
  const visibleTopics = TOPICS.filter((t) => {
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    if (search && !t.topic.toLowerCase().includes(searchLower) && !t.category.toLowerCase().includes(searchLower)) return false;
    return true;
  });

  const coverageCount = (law: DpLaw) => TOPICS.filter((t) => law.refs[t.key]).length;

  const chipStyle = (color: string): CSSProperties => ({
    display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: 4,
    fontSize: 10, fontWeight: 600, background: color + "22", color, whiteSpace: "nowrap", maxWidth: 228,
    overflow: "hidden", textOverflow: "ellipsis",
  });

  const linkButtonStyle: CSSProperties = {
    display: "inline-flex", background: "none", border: "none", cursor: "pointer", padding: 0,
    textDecoration: "underline", textDecorationStyle: "dotted",
  };

  const dot = (status: CellStatus) => {
    const d = STATUS_DOT[status];
    return (
      <span
        title={d.title}
        style={{
          width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: d.bg,
          border: status === "EXEMPT" ? "1px solid var(--c-faint)" : "none", boxSizing: "border-box",
        }}
      />
    );
  };

  const lawColumnCount = visibleLaws.length;

  return (
    <div className="page-pad">
      <StandardHeader
        title="Privacy by article"
        subtitle="How each data protection obligation maps across the privacy laws of your operating locations, so evidence built once to the strictest law can be reused for the others."
      />

      {/* Summary stat — clickable shortcut to Evidence Reuse tab */}
      {reuseOpportunities.length > 0 && (
        <button
          onClick={() => setActiveTab("reuse")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "7px 14px",
            borderRadius: 3, border: `1px solid ${theme.color.success}`, background: theme.color.successBg,
            color: theme.color.successText, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}
        >
          <Recycle size={14} style={{ flexShrink: 0 }} />
          {reuseOpportunities.length} potential evidence reuse {reuseOpportunities.length === 1 ? "opportunity" : "opportunities"} across{" "}
          {applicableLaws.length} applicable {applicableLaws.length === 1 ? "law" : "laws"}: view the evidence reuse tab
        </button>
      )}

      {/* Applicable-law summary cards */}
      {loaded && applicableLaws.length === 0 ? (
        <div style={{ ...emptyFrame, textAlign: "left", maxWidth: 560, marginBottom: 20, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>No data protection laws are mapped to your organisation yet</div>
          <div style={{ fontSize: 12.5, color: theme.color.subtle, lineHeight: 1.6 }}>
            Applicable laws are derived from your Operating Locations in <strong>My Organisation</strong>. Until they are set,
            the table below shows every law the platform tracks for comparison.
          </div>
          <div>
            <button onClick={() => navigate("/org-profile")} style={btnPrimary}>Set up My Organisation</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
          {applicableLaws.map((law) => {
            const counts = countsFor(law);
            const pct = counts && counts.total > 0 ? Math.round((counts.compliant / counts.total) * 100) : 0;
            return (
              <div
                key={law.id}
                title={`${law.label} — ${law.jurisdiction} · ${law.regulator}`}
                style={{ ...card, flex: "0 0 auto", padding: "10px 16px", borderColor: law.color, minWidth: 150, maxWidth: 220 }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: law.color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {law.short}
                  <span style={{ fontSize: 9, background: law.color + "22", padding: "1px 5px", borderRadius: 4 }}>applies</span>
                  {law.id === "jersey" && dpTier && (
                    <span style={{ fontSize: 9, background: "var(--c-accentBg)", color: "var(--c-accentText)", padding: "1px 5px", borderRadius: 4, textTransform: "none", letterSpacing: 0 }}>
                      tier: {dpTier.replace("_", " ")}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.heading }}>{coverageCount(law)}</div>
                <div style={{ fontSize: 10, color: theme.color.subtle }}>obligations addressed · {law.jurisdiction}</div>
                {counts && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", height: 4, borderRadius: 3, overflow: "hidden", gap: 1, marginBottom: 4 }}>
                      {counts.compliant > 0  && <div style={{ flex: counts.compliant,  background: theme.color.successText, opacity: 0.85 }} />}
                      {counts.inProgress > 0 && <div style={{ flex: counts.inProgress, background: theme.color.warningText, opacity: 0.85 }} />}
                      {counts.gap > 0        && <div style={{ flex: counts.gap,        background: theme.color.dangerText,  opacity: 0.85 }} />}
                      {counts.notStarted > 0 && <div style={{ flex: counts.notStarted, background: theme.color.border }} />}
                    </div>
                    <div style={{ fontSize: 9, color: theme.color.muted }}>
                      {pct}% compliant · {counts.gap} gap{counts.gap !== 1 ? "s" : ""}
                      {counts.exempt > 0 && ` · ${counts.exempt} exempt`}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tab selector */}
      <IndexTabs
        items={[
          { id: "table", label: "Mapping table" },
          { id: "reuse", label: "Evidence reuse", count: reuseOpportunities.length > 0 ? reuseOpportunities.length : undefined },
        ]}
        value={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 18 }}
      />

      {activeTab === "table" && (<>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Search obligations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, width: 220, fontSize: 13 }}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ ...inputStyle, fontSize: 13 }}>
          <option value="all">All categories</option>
          {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.color.subtle, cursor: applicableLaws.length > 0 ? "pointer" : "default" }}>
          <input
            type="checkbox"
            checked={showApplicableOnly}
            disabled={applicableLaws.length === 0}
            onChange={(e) => setApplicableOnly(e.target.checked)}
          />
          Applicable laws only ({applicableLaws.length})
        </label>
        {!showApplicableOnly && (
          <>
            <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value as "all" | Region)} style={{ ...inputStyle, fontSize: 13 }}>
              <option value="all">All regions ({LAWS.length} laws)</option>
              {REGIONS.map((r) => <option key={r} value={r}>{r} ({LAWS.filter((l) => l.region === r).length})</option>)}
            </select>
            {applicableLaws.length > 0 && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.color.subtle, cursor: "pointer" }}>
                <input type="checkbox" checked={dimOthers} onChange={(e) => setDimOthers(e.target.checked)} />
                Dim non-applicable laws
              </label>
            )}
          </>
        )}
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
          {visibleTopics.length} of {TOPICS.length} obligations · {lawColumnCount} of {LAWS.length} laws
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", ...card, padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
          <thead>
            {!showApplicableOnly && visibleRegions.length > 1 && (
              <tr style={{ background: "var(--c-panelAlt)" }}>
                <th colSpan={2} style={{ position: "sticky", left: 0, background: "var(--c-panelAlt)", zIndex: 2 }} />
                {visibleRegions.map((region) => (
                  <th
                    key={region}
                    colSpan={visibleLaws.filter((l) => l.region === region).length}
                    style={{ padding: "6px 8px", fontSize: 10, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: `1px solid ${theme.color.border}`, borderLeft: `1px solid ${theme.color.border}`, whiteSpace: "nowrap" }}
                  >
                    {region}
                  </th>
                ))}
                <th />
              </tr>
            )}
            <tr style={{ background: "var(--c-panelAlt)" }}>
              <th style={{ padding: "10px 14px", textAlign: "left", color: theme.color.heading, fontWeight: 700, whiteSpace: "nowrap", minWidth: 220, position: "sticky", left: 0, background: "var(--c-panelAlt)", zIndex: 2, borderBottom: "1px solid var(--c-line)" }}>
                Obligation
              </th>
              <th style={{ padding: "10px 10px", textAlign: "left", color: theme.color.subtle, fontWeight: 600, whiteSpace: "nowrap", fontSize: 11, borderBottom: "1px solid var(--c-line)" }}>
                Category
              </th>
              {visibleLaws.map((law) => {
                const applies = isApplicable(law);
                return (
                  <th
                    key={law.id}
                    title={`${law.label}\n${law.jurisdiction} · ${law.regulator}`}
                    style={{
                      padding: "10px 8px", textAlign: "center", fontWeight: 700, whiteSpace: "nowrap", fontSize: 11,
                      color: applies ? law.color : theme.color.faint,
                      opacity: dimOthers && !applies && applicableLaws.length > 0 ? 0.45 : 1,
                      borderBottom: "1px solid var(--c-line)",
                    }}
                  >
                    {law.short}
                  </th>
                );
              })}
              <th style={{ padding: "10px 8px", textAlign: "center", color: theme.color.subtle, fontWeight: 600, fontSize: 11, borderBottom: "1px solid var(--c-line)" }}>
                Coverage
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleTopics.map((topic) => {
              const covered = visibleLaws.filter((law) => law.refs[topic.key]).length;
              const applicableCovered = applicableLaws.filter((law) => law.refs[topic.key]).length;
              const expanded = expandedTopic === topic.key;
              const rowBg = applicableCovered > 1 ? "var(--c-accentBg)" : "transparent";
              const applicableObligations = applicableLaws
                .map((law) => ({ law, cell: law.refs[topic.key], state: cellStatus(law, law.refs[topic.key]) }))
                .filter((x): x is { law: DpLaw; cell: Cell; state: ObligationState } => !!x.cell && !!x.state);
              return (
                <Fragment key={topic.key}>
                  <tr style={{ background: rowBg }}>
                    <td style={{ padding: "9px 14px", color: theme.color.heading, fontWeight: 500, position: "sticky", left: 0, background: applicableCovered > 1 ? "var(--c-accentBg)" : "var(--c-panel)", zIndex: 1, borderBottom: `1px solid ${theme.color.border}` }}>
                      <button
                        onClick={() => setExpandedTopic(expanded ? null : topic.key)}
                        title={expanded ? "Hide key differences" : "Show key differences between laws"}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: 0, cursor: "pointer", color: theme.color.heading, fontWeight: 500, fontSize: 12, textAlign: "left" }}
                      >
                        {expanded ? <ChevronDown size={13} style={{ flexShrink: 0, color: theme.color.faint }} /> : <ChevronRight size={13} style={{ flexShrink: 0, color: theme.color.faint }} />}
                        {topic.topic}
                      </button>
                    </td>
                    <td style={{ padding: "9px 10px", color: theme.color.subtle, fontSize: 11, whiteSpace: "nowrap", borderBottom: `1px solid ${theme.color.border}` }}>
                      {topic.category}
                    </td>
                    {visibleLaws.map((law) => {
                      const cell = law.refs[topic.key];
                      const applies = isApplicable(law);
                      const state = cellStatus(law, cell);
                      const shared = isSharedCell(law, cell);
                      const dimmed = dimOthers && !applies && applicableLaws.length > 0;
                      const change = cell ? changeLine(cell) : null;
                      const statusLine = state
                        ? shared
                          ? `\nShared obligation: "${state.name}" covers ${rowsSharing(law, cell!.controlId!).join(", ")}. Its status (${STATUS_DOT[state.status].title}) is not shown against this row alone.`
                          : `\n${state.name} — ${STATUS_DOT[state.status].title}${state.headline ? `\n${state.headline}` : ""}`
                        : "";
                      const tooltip = cell
                        ? `${law.label}: ${cell.ref}${change ? `\n${change}` : ""}${statusLine}`
                        : `Not mapped for ${law.label}: the law may not address it, or it has not been mapped yet`;
                      return (
                        <td key={law.id} title={tooltip} style={{ padding: "9px 8px", textAlign: "center", opacity: dimmed ? 0.35 : 1, borderBottom: `1px solid ${theme.color.border}` }}>
                          {cell ? (() => {
                            const chip = (
                              <span style={{ ...chipStyle(law.color), ...(shared && state ? { border: `1px dashed ${law.color}` } : {}) }}>
                                {state && !shared && dot(state.status)}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{cell.ref}</span>
                              </span>
                            );
                            const since = cell.inForce && (
                              <div style={{ fontSize: 9, color: theme.color.muted, marginTop: 2, whiteSpace: "nowrap" }}>since {cell.inForce}</div>
                            );
                            return applies && state ? (
                              <>
                                <button onClick={() => navigate("/legal")} style={linkButtonStyle} title={`${tooltip}\nOpen Legal Obligations`}>
                                  {chip}
                                </button>
                                {since}
                              </>
                            ) : <>{chip}{since}</>;
                          })() : (
                            <span style={{ color: theme.color.faint, fontSize: 14 }}>·</span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ padding: "9px 8px", textAlign: "center", borderBottom: `1px solid ${theme.color.border}` }}>
                      <span
                        title={`${covered} of ${lawColumnCount} displayed laws address this obligation`}
                        style={{
                          display: "inline-block", width: 22, height: 22, borderRadius: "50%",
                          background: covered >= Math.max(2, Math.ceil(lawColumnCount * 0.75)) ? theme.color.successBg : covered >= 2 ? theme.color.warningBg : theme.color.panelAlt,
                          color: covered >= Math.max(2, Math.ceil(lawColumnCount * 0.75)) ? theme.color.successText : covered >= 2 ? theme.color.warningText : theme.color.faint,
                          fontSize: 11, fontWeight: 700, lineHeight: "22px", textAlign: "center",
                        }}
                      >
                        {covered}
                      </span>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={lawColumnCount + 3} style={{ padding: "12px 18px 14px 34px", background: "var(--c-panelAlt)", borderBottom: `1px solid ${theme.color.border}` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                          Key differences
                        </div>
                        <p style={{ margin: 0, fontSize: 12, color: theme.color.text, lineHeight: 1.65, maxWidth: 980 }}>{topic.note}</p>
                        {applicableObligations.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                              Your tracked obligations
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {applicableObligations.map(({ law, cell, state }) => {
                                const shared = isSharedCell(law, cell);
                                return (
                                  <button
                                    key={law.id}
                                    onClick={() => navigate("/legal")}
                                    title={shared
                                      ? `${law.label}: ${cell.ref}\nShared obligation: "${state.name}" covers ${rowsSharing(law, cell.controlId!).join(", ")}, so its status is not shown for this row alone.\nOpen Legal Obligations`
                                      : `${law.label}: ${cell.ref}\n${state.name} — ${STATUS_DOT[state.status].title}${state.headline ? `\n${state.headline}` : ""}\nOpen Legal Obligations`}
                                    style={linkButtonStyle}
                                  >
                                    <span style={{ ...chipStyle(law.color), maxWidth: 420, padding: "3px 8px", fontSize: 11, ...(shared ? { border: `1px dashed ${law.color}` } : {}) }}>
                                      {!shared && dot(state.status)}
                                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{law.short} · {state.name}{shared ? " (shared)" : ""}</span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {visibleTopics.length === 0 && (
          <div style={{ ...emptyFrame, margin: 12, padding: "28px 20px", color: theme.color.subtle, fontSize: 13 }}>
            No obligations match your search.
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ marginTop: 14, display: "flex", gap: 20, fontSize: 11, color: theme.color.subtle, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: "var(--c-accentBg)", border: `1px solid ${theme.color.border}`, display: "inline-block" }} />
          Highlighted row = 2+ of your applicable laws impose this obligation (evidence reuse opportunity)
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {dot("COMPLIANT")} {dot("IN_PROGRESS")} {dot("GAP")} {dot("NOT_STARTED")} = Legal Obligations status
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {dot("EXEMPT")} = exempt by threshold (not counted)
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 10, borderRadius: 2, border: `1px dashed ${theme.color.subtle}`, display: "inline-block" }} />
          = shared obligation: one register entry covers several rows, so no status is shown on any of them
        </span>
        <span>"since" = date the law took this wording</span>
        <span>· = not mapped (the law may not address it)</span>
        <span>Coverage = displayed laws that address the obligation</span>
        <span>Click an obligation for key differences between the laws</span>
      </div>

      </>)}

      {/* Evidence Reuse panel */}
      {activeTab === "reuse" && (
        <div>
          {reuseOpportunities.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "36px 24px", color: theme.color.subtle, fontSize: 13 }}>
              {applicableLaws.length < 2
                ? "Evidence reuse needs at least two applicable data protection laws — add operating locations in My Organisation to compare."
                : "No reuse opportunities found yet — mark obligations as compliant in Legal Obligations to see where evidence can be shared across laws."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {reuseOpportunities.map(({ topic, compliant, gaps }) => {
                const first = compliant[0];
                const firstGap = gaps[0];
                const reuseNote = `Evidence for ${first.law.short} ${first.cell.ref} likely satisfies ${firstGap.law.short} ${firstGap.cell.ref} — review the jurisdiction-specific differences before duplicating effort.`;
                return (
                  <div key={topic.key} style={{ ...card, padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>{topic.topic}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 3, background: "var(--c-panelAlt)", color: theme.color.subtle, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        {topic.category}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.successText, marginBottom: 6 }}>Already compliant under:</div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {compliant.map(({ law, cell, state }) => (
                            <button key={law.id} onClick={() => navigate("/legal")} title={`${law.label}: ${cell.ref}\n${state.name}`} style={linkButtonStyle}>
                              <span style={{ ...chipStyle(law.color), padding: "3px 8px", fontSize: 11, maxWidth: 320 }}>
                                {dot("COMPLIANT")}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{law.short} {cell.ref}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.warningText, marginBottom: 6 }}>Can satisfy:</div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {gaps.map(({ law, cell, state }) => (
                            <span key={law.id} title={`${law.label}: ${cell.ref}\n${state.name} — ${STATUS_DOT[state.status].title}`} style={{ ...chipStyle(law.color), padding: "3px 8px", fontSize: 11, maxWidth: 320 }}>
                              {dot(state.status)}
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{law.short} {cell.ref}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: theme.color.subtle, fontStyle: "italic", marginBottom: 10, paddingLeft: 10, borderLeft: `3px solid ${theme.color.border}` }}>
                      {reuseNote}
                    </div>
                    <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6, marginBottom: 12 }}>{topic.note}</div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {gaps.map(({ law, cell }) => (
                        <button
                          key={law.id}
                          onClick={() => navigate("/legal")}
                          style={{ padding: "5px 12px", borderRadius: 3, border: `1px solid ${law.color}55`, background: "transparent", color: law.color, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          View {law.short} {cell.ref}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Info footer */}
      <div style={{ marginTop: 20, ...card, padding: "14px 18px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>About this mapping</div>
        <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
          Applicable laws are derived from your Operating Locations under <strong>My Organisation</strong>; status dots come from
          your <strong>Legal Obligations</strong> register (only where the row has an entry of its own: an entry covering
          several rows is marked shared and colours none of them), and obligations the data-protection threshold engine marks exempt for an
          organisation of your size are shown as not applicable. Citations are to the primary legislation ({LAW_BY_ID.size} laws,{" "}
          {TOPICS.length} obligations); where a law has recently been re-enacted or renumbered the cell carries a descriptive label
          instead. The mapping is a navigation aid, not legal advice — confirm jurisdiction-specific requirements with your adviser.
        </p>
      </div>
    </div>
  );
}
